import { useEffect, useMemo, useRef, useState } from "react"
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Check,
  Copy,
  ChevronsUpDown,
  Pencil,
  Trash2,
} from "lucide-react"

import { SEO } from "@/components/SEO"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { toast } from "@/components/ui/use-toast"
import { getApiBaseUrl } from "@/lib/api"
import { DatePicker } from "../../components/ui/date-picker"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

type TenantOption = { id: number; name: string }

type TaxRegime = "New" | "Old"

type EmploymentType = string

type TaxSlabRow = {
  id: string
  from: number
  to: number | null
  ratePercent: number
}

type SurchargeRow = {
  id: string
  from: number
  to: number | null
  ratePercent: number
}

type IncomeTaxMethod = {
  id: number
  name: string
  description: string
  employmentTypes: EmploymentType[]
  taxRegime: TaxRegime
  country: string
  effectiveDate: string
  updatedOn: string
  status: "Active" | "Inactive"
  standardDeduction: number
  minAge: number | null
  maxAge: number | null
  tenantIds: number[] // empty => all tenants
  slabs: TaxSlabRow[]
  surcharges: SurchargeRow[]
  educationCessPercent: number
}

type PfRule = {
  id: number
  name: string
  description: string
  country: string
  effectiveDate: string
  updatedOn: string
  status: "Active" | "Inactive"
  employmentTypes: EmploymentType[]
  tenantIds: number[]
  minAge: number | null
  maxAge: number | null
  formulas: PfFormulaRow[]
}

type PfFormulaRow = {
  id: string
  account: string
  formulaY15k: string
  formulaY: string
}

type EsiRule = {
  id: number
  name: string
  description: string
  country: string
  effectiveDate: string
  updatedOn: string
  status: "Active" | "Inactive"
  employmentTypes: EmploymentType[]
  tenantIds: number[]
  minAge: number | null
  maxAge: number | null
  formulas: EsiFormulaRow[]
}

type EsiFormulaRow = {
  id: string
  account: string
  monthlyFormula: string
}

type PtSlabRow = {
  id: string
  from: number
  to: number | null
  amount: number
}

type PtRule = {
  id: number
  stateName: string
  country: string
  effectiveFrom: string
  updatedOn: string
  frequency: "Monthly" | "Quarterly" | "Half-yearly"
  minAge: number | null
  maxAge: number | null
  employmentTypes: EmploymentType[]
  tenantIds: number[] // empty => all tenants
  slabs: PtSlabRow[]
}

type LwfSlabRow = {
  id: string
  from: number
  to: number | null
  employeeAmount: number
  employerAmount: number
}

type LwfRule = {
  id: number
  stateName: string
  country: string
  effectiveFrom: string
  updatedOn: string
  frequency: "Monthly" | "Quarterly" | "Half-yearly"
  minAge: number | null
  maxAge: number | null
  employmentTypes: EmploymentType[]
  tenantIds: number[] // empty => all tenants
  slabs: LwfSlabRow[]
}

function uid(prefix: string) {
  return `${prefix}-${Math.random().toString(16).slice(2)}-${Date.now()}`
}

function formatDdMmmYyyy(dateISO: string) {
  // accepts YYYY-MM-DD or ISO; formats as dd-mmm-yyyy (e.g., 28-Jan-2026)
  const d =
    /^\d{4}-\d{2}-\d{2}$/.test(dateISO)
      ? (() => {
          const [y, m, day] = dateISO.split("-").map(Number)
          return new Date(y, m - 1, day) // local date (no timezone shift)
        })()
      : new Date(dateISO)
  if (Number.isNaN(d.getTime())) return dateISO
  const dd = String(d.getDate()).padStart(2, "0")
  const mmm = d.toLocaleString("en-US", { month: "short" })
  const yyyy = String(d.getFullYear())
  return `${dd}-${mmm}-${yyyy}`
}

function toLocalYmd(value: string) {
  const v = String(value || "")
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return ""
  // Backend currently serializes Postgres DATE as a UTC timestamp (Z)
  // representing IST midnight (e.g. 2025-03-31T18:30:00.000Z == 2025-04-01 IST).
  // To show the *stored* date consistently, normalize using a fixed IST offset.
  if (v.includes("T") && v.endsWith("Z")) {
    const istMs = d.getTime() + 330 * 60 * 1000 // +05:30
    const ist = new Date(istMs)
    const yyyy = ist.getUTCFullYear()
    const mm = String(ist.getUTCMonth() + 1).padStart(2, "0")
    const dd = String(ist.getUTCDate()).padStart(2, "0")
    return `${yyyy}-${mm}-${dd}`
  }

  // Fallback: local date parts
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}

export function PayrollSettingsPage() {
  const API_BASE = getApiBaseUrl()

  const [tenants, setTenants] = useState<TenantOption[]>([])
  const [tenantSearch, setTenantSearch] = useState("")
  const [employmentTypeOptions, setEmploymentTypeOptions] = useState<string[]>([
    "Consultant",
    "Contractor",
    "Fixed Term",
    "Intern",
    "Permanent",
  ])
  const [employmentTypeSearch, setEmploymentTypeSearch] = useState("")

  const [methods, setMethods] = useState<IncomeTaxMethod[]>([])
  const [methodsLoading, setMethodsLoading] = useState(false)
  const [methodsSaving, setMethodsSaving] = useState(false)
  const [methodsApiError, setMethodsApiError] = useState<string | null>(null)
  const [methodsSearch, setMethodsSearch] = useState("")
  const nameInputRef = useRef<HTMLInputElement | null>(null)
  const [isIncomeTaxFormOpen, setIsIncomeTaxFormOpen] = useState(false)
  const [editingMethodId, setEditingMethodId] = useState<number | null>(null)
  const incomeTaxTopRef = useRef<HTMLDivElement | null>(null)
  const [methodsPageSize, setMethodsPageSize] = useState(10)
  const [methodsPage, setMethodsPage] = useState(1) // 1-based
  const [methodsTotal, setMethodsTotal] = useState(0)
  const [methodsTotalPages, setMethodsTotalPages] = useState(1)

  const [pfRules, setPfRules] = useState<PfRule[]>(() => {
    return []
  })
  const [pfLoading, setPfLoading] = useState(false)
  const [pfSaving, setPfSaving] = useState(false)
  const [pfApiError, setPfApiError] = useState<string | null>(null)
  const [pfSearch, setPfSearch] = useState("")
  const [pfPageSize, setPfPageSize] = useState(10)
  const [pfPage, setPfPage] = useState(1)
  const [pfTotal, setPfTotal] = useState(0)
  const [pfTotalPages, setPfTotalPages] = useState(1)
  const [isPfFormOpen, setIsPfFormOpen] = useState(false)
  const [editingPfId, setEditingPfId] = useState<number | null>(null)
  const pfTopRef = useRef<HTMLDivElement | null>(null)
  const [pfErrors, setPfErrors] = useState<Record<string, string>>({})

  const [pfName, setPfName] = useState("")
  const [pfDescription, setPfDescription] = useState("")
  const [pfStatus, setPfStatus] = useState<"Active" | "Inactive">("Active")
  const [pfEmploymentTypes, setPfEmploymentTypes] = useState<EmploymentType[]>(["Permanent"])
  const [pfEmploymentTypeSearch, setPfEmploymentTypeSearch] = useState("")
  const [pfCountry, setPfCountry] = useState("India")
  const [pfEffectiveDate, setPfEffectiveDate] = useState<string>(new Date().toISOString().slice(0, 10))
  const [pfMinAge, setPfMinAge] = useState<number | null>(null)
  const [pfMaxAge, setPfMaxAge] = useState<number | null>(null)
  const [pfTenantIds, setPfTenantIds] = useState<number[]>([])
  const [pfTenantSearch, setPfTenantSearch] = useState("")
  const [pfFormulas, setPfFormulas] = useState<PfFormulaRow[]>([])

  // ===== ESI (UI-only, mirrors PF) =====
  const [esiRules, setEsiRules] = useState<EsiRule[]>(() => [])
  const [esiLoading, setEsiLoading] = useState(false)
  const [esiSaving, setEsiSaving] = useState(false)
  const [esiApiError, setEsiApiError] = useState<string | null>(null)
  const [esiSearch, setEsiSearch] = useState("")
  const [esiPageSize, setEsiPageSize] = useState(10)
  const [esiPage, setEsiPage] = useState(1)
  const [esiTotal, setEsiTotal] = useState(0)
  const [esiTotalPages, setEsiTotalPages] = useState(1)
  const [isEsiFormOpen, setIsEsiFormOpen] = useState(false)
  const [editingEsiId, setEditingEsiId] = useState<number | null>(null)
  const esiTopRef = useRef<HTMLDivElement | null>(null)
  const [esiErrors, setEsiErrors] = useState<Record<string, string>>({})

  const [esiName, setEsiName] = useState("")
  const [esiDescription, setEsiDescription] = useState("")
  const [esiStatus, setEsiStatus] = useState<"Active" | "Inactive">("Active")
  const [esiEmploymentTypes, setEsiEmploymentTypes] = useState<EmploymentType[]>(["Permanent"])
  const [esiEmploymentTypeSearch, setEsiEmploymentTypeSearch] = useState("")
  const [esiCountry, setEsiCountry] = useState("India")
  const [esiEffectiveDate, setEsiEffectiveDate] = useState<string>(new Date().toISOString().slice(0, 10))
  const [esiMinAge, setEsiMinAge] = useState<number | null>(null)
  const [esiMaxAge, setEsiMaxAge] = useState<number | null>(null)
  const [esiTenantIds, setEsiTenantIds] = useState<number[]>([])
  const [esiTenantSearch, setEsiTenantSearch] = useState("")
  const [esiFormulas, setEsiFormulas] = useState<EsiFormulaRow[]>([])

  const [ptStates, setPtStates] = useState<string[]>([])
  const [ptRules, setPtRules] = useState<PtRule[]>([])
  const [ptLoading, setPtLoading] = useState(false)
  const [ptSaving, setPtSaving] = useState(false)
  const [ptApiError, setPtApiError] = useState<string | null>(null)
  const [ptSearch, setPtSearch] = useState("")
  const [ptPageSize, setPtPageSize] = useState(10)
  const [ptPage, setPtPage] = useState(1)
  const [ptTotal, setPtTotal] = useState(0)
  const [ptTotalPages, setPtTotalPages] = useState(1)
  const [isPtFormOpen, setIsPtFormOpen] = useState(false)
  const [editingPtId, setEditingPtId] = useState<number | null>(null)
  const [ptStateName, setPtStateName] = useState("")
  const [ptStateOpen, setPtStateOpen] = useState(false)
  const [ptStateSearch, setPtStateSearch] = useState("")
  const [ptEmploymentTypes, setPtEmploymentTypes] = useState<EmploymentType[]>(["Permanent"])
  const [ptEmploymentTypeSearch, setPtEmploymentTypeSearch] = useState("")
  const [ptCountry, setPtCountry] = useState("India")
  const [ptEffectiveFrom, setPtEffectiveFrom] = useState<string>(new Date().toISOString().slice(0, 10))
  const [ptFrequency, setPtFrequency] = useState<PtRule["frequency"]>("Monthly")
  const [ptMinAge, setPtMinAge] = useState<number | null>(null)
  const [ptMaxAge, setPtMaxAge] = useState<number | null>(null)
  const [ptTenantIds, setPtTenantIds] = useState<number[]>([])
  const [ptTenantSearch, setPtTenantSearch] = useState("")
  const [ptSlabs, setPtSlabs] = useState<PtSlabRow[]>([
    { id: uid("pt"), from: 0, to: null, amount: 0 },
  ])

  const [lwfRules, setLwfRules] = useState<LwfRule[]>([])
  const [lwfLoading, setLwfLoading] = useState(false)
  const [lwfSaving, setLwfSaving] = useState(false)
  const [lwfApiError, setLwfApiError] = useState<string | null>(null)
  const [lwfSearch, setLwfSearch] = useState("")
  const [lwfPageSize, setLwfPageSize] = useState(10)
  const [lwfPage, setLwfPage] = useState(1)
  const [lwfTotal, setLwfTotal] = useState(0)
  const [lwfTotalPages, setLwfTotalPages] = useState(1)
  const [isLwfFormOpen, setIsLwfFormOpen] = useState(false)
  const [editingLwfId, setEditingLwfId] = useState<number | null>(null)
  const [lwfStateName, setLwfStateName] = useState("")
  const [lwfStateOpen, setLwfStateOpen] = useState(false)
  const [lwfStateSearch, setLwfStateSearch] = useState("")
  const [lwfEmploymentTypes, setLwfEmploymentTypes] = useState<EmploymentType[]>(["Permanent"])
  const [lwfEmploymentTypeSearch, setLwfEmploymentTypeSearch] = useState("")
  const [lwfCountry, setLwfCountry] = useState("India")
  const [lwfEffectiveFrom, setLwfEffectiveFrom] = useState<string>(new Date().toISOString().slice(0, 10))
  const [lwfFrequency, setLwfFrequency] = useState<LwfRule["frequency"]>("Monthly")
  const [lwfMinAge, setLwfMinAge] = useState<number | null>(null)
  const [lwfMaxAge, setLwfMaxAge] = useState<number | null>(null)
  const [lwfTenantIds, setLwfTenantIds] = useState<number[]>([])
  const [lwfTenantSearch, setLwfTenantSearch] = useState("")
  const [lwfSlabs, setLwfSlabs] = useState<LwfSlabRow[]>([
    { id: uid("lwf"), from: 0, to: null, employeeAmount: 0, employerAmount: 0 },
  ])

  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [employmentTypes, setEmploymentTypes] = useState<EmploymentType[]>(["Permanent"])
  const [taxRegime, setTaxRegime] = useState<TaxRegime>("New")
  const [country, setCountry] = useState("India")
  const [effectiveDate, setEffectiveDate] = useState<string>(new Date().toISOString().slice(0, 10))
  const [standardDeduction, setStandardDeduction] = useState<string>("0")
  const [minAge, setMinAge] = useState<number | null>(null)
  const [maxAge, setMaxAge] = useState<number | null>(null)
  const [selectedTenantIds, setSelectedTenantIds] = useState<number[]>([])

  const [slabs, setSlabs] = useState<TaxSlabRow[]>([
    { id: uid("slab"), from: 0, to: 250000, ratePercent: 0 },
    { id: uid("slab"), from: 250001, to: 500000, ratePercent: 5 },
  ])
  const [surcharges, setSurcharges] = useState<SurchargeRow[]>([
    { id: uid("sur"), from: 5000000, to: null, ratePercent: 10 },
  ])
  const [educationCessPercent, setEducationCessPercent] = useState<number>(4)

  const [errors, setErrors] = useState<Record<string, string>>({})

  function apiPfToUi(row: any): PfRule {
    const rawStatus = String(row?.status ?? "").toLowerCase()
    const effective = String(row?.effectiveDate ?? row?.effective_date ?? "").slice(0, 10)
    const updatedOn = String(row?.updatedAt ?? row?.updated_at ?? row?.createdAt ?? row?.created_at ?? "")
    const formulas = Array.isArray(row?.formulas) ? row.formulas : []
    return {
      id: Number(row?.id),
      name: String(row?.name ?? ""),
      description: String(row?.description ?? ""),
      country: String(row?.country ?? "India"),
      effectiveDate: effective || new Date().toISOString().slice(0, 10),
      updatedOn: updatedOn || new Date().toISOString(),
      status: rawStatus === "inactive" ? "Inactive" : "Active",
      employmentTypes: Array.isArray(row?.employmentTypes ?? row?.employment_types)
        ? (row?.employmentTypes ?? row?.employment_types)
        : [],
      tenantIds: Array.isArray(row?.tenantIds ?? row?.tenant_ids) ? (row?.tenantIds ?? row?.tenant_ids) : [],
      minAge: row?.minAge ?? row?.min_age ?? null,
      maxAge: row?.maxAge ?? row?.max_age ?? null,
      formulas: formulas.map((f: any) => ({
        id: uid("pf"),
        account: String(f?.account ?? ""),
        formulaY15k: String(f?.formulaY15k ?? f?.formula_y_15k ?? ""),
        formulaY: String(f?.formulaY ?? f?.formula_y ?? ""),
      })),
    }
  }

  async function loadPfCalculations(opts?: { page?: number; limit?: number; q?: string }) {
    const page = opts?.page ?? pfPage
    const limit = opts?.limit ?? pfPageSize
    const q = (opts?.q ?? pfSearch).trim()

    setPfLoading(true)
    setPfApiError(null)
    try {
      const params = new URLSearchParams()
      params.set("page", String(page))
      params.set("limit", String(limit))
      if (q) params.set("q", q)

      const res = await fetch(`${API_BASE}/client-pfcalculation?${params.toString()}`)
      const json = await res.json()
      if (!res.ok) throw new Error(json?.message || "Failed to load PF calculations.")

      const data = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : []
      const meta = json?.meta || {}
      const total = Number(meta?.total ?? data.length) || data.length
      const last = Number(meta?.lastPage ?? 1) || 1

      setPfRules(data.map(apiPfToUi))
      setPfTotal(total)
      setPfTotalPages(last)
      setPfPage(Math.min(Math.max(1, page), last))
    } catch (e: any) {
      setPfRules([])
      setPfTotal(0)
      setPfTotalPages(1)
      setPfApiError(e?.message || "Failed to load PF calculations.")
      toast({
        title: "Failed to load",
        description: e?.message || "Failed to load PF calculations.",
        variant: "destructive",
      })
    } finally {
      setPfLoading(false)
    }
  }

  useEffect(() => {
    setPfPage(1)
  }, [pfSearch, pfPageSize])

  useEffect(() => {
    loadPfCalculations({ page: pfPage, limit: pfPageSize, q: pfSearch })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE, pfPage, pfPageSize, pfSearch])

  function apiEsiToUi(row: any): EsiRule {
    const rawStatus = String(row?.status ?? "").toLowerCase()
    const effective = String(row?.effectiveDate ?? row?.effective_date ?? "").slice(0, 10)
    const updatedOn = String(row?.updatedAt ?? row?.updated_at ?? row?.createdAt ?? row?.created_at ?? "")
    const formulas = Array.isArray(row?.formulas) ? row.formulas : []
    return {
      id: Number(row?.id),
      name: String(row?.name ?? ""),
      description: String(row?.description ?? ""),
      country: String(row?.country ?? "India"),
      effectiveDate: effective || new Date().toISOString().slice(0, 10),
      updatedOn: updatedOn || new Date().toISOString(),
      status: rawStatus === "inactive" ? "Inactive" : "Active",
      employmentTypes: Array.isArray(row?.employmentTypes ?? row?.employment_types)
        ? (row?.employmentTypes ?? row?.employment_types)
        : [],
      tenantIds: Array.isArray(row?.tenantIds ?? row?.tenant_ids) ? (row?.tenantIds ?? row?.tenant_ids) : [],
      minAge: row?.minAge ?? row?.min_age ?? null,
      maxAge: row?.maxAge ?? row?.max_age ?? null,
      formulas: formulas.map((f: any) => ({
        id: uid("esi"),
        account: String(f?.account ?? ""),
        monthlyFormula: String(f?.monthlyFormula ?? f?.monthly_formula ?? ""),
      })),
    }
  }

  async function loadEsiCalculations(opts?: { page?: number; limit?: number; q?: string }) {
    const page = opts?.page ?? esiPage
    const limit = opts?.limit ?? esiPageSize
    const q = (opts?.q ?? esiSearch).trim()

    setEsiLoading(true)
    setEsiApiError(null)
    try {
      const params = new URLSearchParams()
      params.set("page", String(page))
      params.set("limit", String(limit))
      if (q) params.set("q", q)

      const res = await fetch(`${API_BASE}/client-esicalculation?${params.toString()}`)
      const json = await res.json()
      if (!res.ok) throw new Error(json?.message || "Failed to load ESI calculations.")

      const data = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : []
      const meta = json?.meta || {}
      const total = Number(meta?.total ?? data.length) || data.length
      const last = Number(meta?.lastPage ?? 1) || 1

      setEsiRules(data.map(apiEsiToUi))
      setEsiTotal(total)
      setEsiTotalPages(last)
      setEsiPage(Math.min(Math.max(1, page), last))
    } catch (e: any) {
      setEsiRules([])
      setEsiTotal(0)
      setEsiTotalPages(1)
      setEsiApiError(e?.message || "Failed to load ESI calculations.")
      toast({
        title: "Failed to load",
        description: e?.message || "Failed to load ESI calculations.",
        variant: "destructive",
      })
    } finally {
      setEsiLoading(false)
    }
  }

  useEffect(() => {
    loadEsiCalculations({ page: esiPage, limit: esiPageSize, q: esiSearch })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE, esiPage, esiPageSize, esiSearch])

  useEffect(() => {
    setEsiPage(1)
  }, [esiSearch, esiPageSize])

  function getDuplicatePfName(baseName: string) {
    const trimmed = baseName.trim() || "Untitled"
    const existing = new Set(pfRules.map((m) => m.name.trim().toLowerCase()))
    const first = `${trimmed} (Copy)`
    if (!existing.has(first.toLowerCase())) return first
    for (let i = 2; i <= 50; i++) {
      const candidate = `${trimmed} (Copy ${i})`
      if (!existing.has(candidate.toLowerCase())) return candidate
    }
    return `${trimmed} (Copy ${Date.now()})`
  }

  function scrollPfTop() {
    pfTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  function filteredPfTenants() {
    const q = pfTenantSearch.trim().toLowerCase()
    if (!q) return tenants
    return tenants.filter((t) => `${t.id} ${t.name}`.toLowerCase().includes(q))
  }

  const filteredPfEmploymentTypes = useMemo(() => {
    const q = pfEmploymentTypeSearch.trim().toLowerCase()
    if (!q) return employmentTypeOptions
    return employmentTypeOptions.filter((t) => t.toLowerCase().includes(q))
  }, [employmentTypeOptions, pfEmploymentTypeSearch])

  function togglePfEmploymentType(v: EmploymentType) {
    setPfEmploymentTypes((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]))
  }

  function selectAllPfEmploymentTypes() {
    setPfEmploymentTypes(employmentTypeOptions.slice())
  }

  function clearAllPfEmploymentTypes() {
    setPfEmploymentTypes([])
  }

  function togglePfTenant(id: number) {
    setPfTenantIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  function selectAllPfTenants() {
    setPfTenantIds(tenants.map((t) => t.id))
  }

  function clearAllPfTenants() {
    setPfTenantIds([])
  }

  function resetPfForm() {
    setPfName("")
    setPfDescription("")
    setPfStatus("Active")
    setPfEmploymentTypes(["Permanent"])
    setPfEmploymentTypeSearch("")
    setPfCountry("India")
    setPfEffectiveDate(new Date().toISOString().slice(0, 10))
    setPfMinAge(null)
    setPfMaxAge(null)
    setPfTenantIds([])
    setPfTenantSearch("")
    setPfErrors({})
    // default formula grid (same for every new form)
    setPfFormulas([
      {
        id: uid("pf"),
        account: "EPF (A/c 1)",
        formulaY15k: "=MIN(SUM(components)*12%,15000*12%)",
        formulaY: "=SUM(components)*12%",
      },
      {
        id: uid("pf"),
        account: "EPF (A/c 1)",
        formulaY15k: "=MIN(SUM(components)*3.67%,15000*3.67%)",
        formulaY: "=SUM(components)*3.67%",
      },
      {
        id: uid("pf"),
        account: "EPS (A/c 10)",
        formulaY15k: "=MIN(SUM(components)*8.33%,15000*8.33%)",
        formulaY: "=MIN(SUM(components)*8.33%,15000*8.33%)",
      },
      {
        id: uid("pf"),
        account: "EDLI (A/c 21)",
        formulaY15k: "=MIN(SUM(components)*.50%,15000*.50%)",
        formulaY: "=SUM(components)*.50%",
      },
      {
        id: uid("pf"),
        account: "EDLI Admin (A/c 22)",
        formulaY15k: "=MIN(SUM(components)*.01%,15000*.01%,15)",
        formulaY: "=MIN(SUM(components)*.01%,15)",
      },
      {
        id: uid("pf"),
        account: "EPF Admin (A/c 2)",
        formulaY15k: "=MIN(SUM(components)*.50%,15000*.50%)",
        formulaY: "=SUM(components)*.50%",
      },
    ])
  }

  function openNewPf() {
    setEditingPfId(null)
    resetPfForm()
    setIsPfFormOpen(true)
    queueMicrotask(() => scrollPfTop())
  }

  function openEditPf(row: PfRule) {
    setEditingPfId(row.id)
    setPfName(row.name)
    setPfDescription(row.description)
    setPfStatus(row.status)
    setPfEmploymentTypes(row.employmentTypes.slice())
    setPfEmploymentTypeSearch("")
    setPfCountry(row.country)
    setPfEffectiveDate(row.effectiveDate)
    setPfMinAge(row.minAge)
    setPfMaxAge(row.maxAge)
    setPfTenantIds(row.tenantIds.slice())
    setPfTenantSearch("")
    setPfErrors({})
    setPfFormulas(row.formulas.map((r) => ({ ...r, id: uid("pf") })))
    setIsPfFormOpen(true)
    queueMicrotask(() => scrollPfTop())
  }

  function duplicatePf(row: PfRule) {
    setEditingPfId(null)
    setPfName(getDuplicatePfName(row.name))
    setPfDescription(row.description)
    setPfStatus(row.status)
    setPfEmploymentTypes(row.employmentTypes.slice())
    setPfEmploymentTypeSearch("")
    setPfCountry(row.country)
    setPfEffectiveDate(row.effectiveDate)
    setPfMinAge(row.minAge)
    setPfMaxAge(row.maxAge)
    setPfTenantIds(row.tenantIds.slice())
    setPfTenantSearch("")
    setPfErrors({})
    setPfFormulas(row.formulas.map((r) => ({ ...r, id: uid("pf") })))
    setIsPfFormOpen(true)
    queueMicrotask(() => scrollPfTop())
  }

  function validatePf() {
    const next: Record<string, string> = {}
    if (!pfName.trim()) next.pfName = "Name is required."
    if (pfEmploymentTypes.length === 0) next.pfEmploymentTypes = "Select at least one employment type."
    if (!pfCountry.trim()) next.pfCountry = "Country is required."
    if (!pfEffectiveDate) next.pfEffectiveDate = "Effective date is required."
    if (pfMinAge !== null && pfMinAge < 0) next.pfAge = "Min age cannot be negative."
    if (pfMaxAge !== null && pfMaxAge < 0) next.pfAge = "Max age cannot be negative."
    if (pfMinAge !== null && pfMaxAge !== null && pfMaxAge < pfMinAge) next.pfAge = "Max age must be ≥ Min age."
    if (pfFormulas.length === 0) next.pfFormulas = "PF formula grid is required."
    setPfErrors(next)
    return Object.keys(next).length === 0
  }

  function savePf() {
    ;(async () => {
      if (!validatePf()) return

      setPfSaving(true)
      setPfApiError(null)

      const payload = {
        name: pfName.trim(),
        description: pfDescription.trim(),
        status: pfStatus === "Inactive" ? "inactive" : "active",
        country: pfCountry.trim(),
        effectiveDate: pfEffectiveDate,
        minAge: pfMinAge,
        maxAge: pfMaxAge,
        employmentTypes: pfEmploymentTypes,
        tenantIds: pfTenantIds,
        formulas: pfFormulas.map((r) => ({
          account: r.account,
          formulaY15k: r.formulaY15k,
          formulaY: r.formulaY,
        })),
      }

      try {
        if (editingPfId) {
          const res = await fetch(`${API_BASE}/client-pfcalculation/${editingPfId}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          })
          const json = await res.json()
          if (!res.ok) throw new Error(json?.message || "Failed to update PF calculation.")
        } else {
          const res = await fetch(`${API_BASE}/client-pfcalculation`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          })
          const json = await res.json()
          if (!res.ok) throw new Error(json?.message || "Failed to create PF calculation.")
        }

        setIsPfFormOpen(false)
        setEditingPfId(null)
        setPfSearch("")
        setPfPage(1)
        await loadPfCalculations({ page: 1, limit: pfPageSize, q: "" })
        queueMicrotask(() => scrollPfTop())
      } catch (e: any) {
        const message = e?.message || "Failed to save."
        setPfApiError(message)
        const lower = String(message).toLowerCase()
        const isDuplicate = lower.includes("duplicate") || lower.includes("already exists") || lower.includes("conflict")
        toast({
          title: isDuplicate ? "Duplicate record" : "Save failed",
          description: isDuplicate ? "A record with the same name already exists." : message,
          variant: "destructive",
        })
      } finally {
        setPfSaving(false)
      }
    })()
  }

  function getDuplicateEsiName(baseName: string) {
    const trimmed = baseName.trim() || "Untitled"
    const existing = new Set(esiRules.map((m) => m.name.trim().toLowerCase()))
    const first = `${trimmed} (Copy)`
    if (!existing.has(first.toLowerCase())) return first
    for (let i = 2; i <= 50; i++) {
      const candidate = `${trimmed} (Copy ${i})`
      if (!existing.has(candidate.toLowerCase())) return candidate
    }
    return `${trimmed} (Copy ${Date.now()})`
  }

  function scrollEsiTop() {
    esiTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  function filteredEsiTenants() {
    const q = esiTenantSearch.trim().toLowerCase()
    if (!q) return tenants
    return tenants.filter((t) => `${t.id} ${t.name}`.toLowerCase().includes(q))
  }

  const filteredEsiEmploymentTypes = useMemo(() => {
    const q = esiEmploymentTypeSearch.trim().toLowerCase()
    if (!q) return employmentTypeOptions
    return employmentTypeOptions.filter((t) => t.toLowerCase().includes(q))
  }, [employmentTypeOptions, esiEmploymentTypeSearch])

  function toggleEsiEmploymentType(v: EmploymentType) {
    setEsiEmploymentTypes((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]))
  }

  function selectAllEsiEmploymentTypes() {
    setEsiEmploymentTypes(employmentTypeOptions.slice())
  }

  function clearAllEsiEmploymentTypes() {
    setEsiEmploymentTypes([])
  }

  function toggleEsiTenant(id: number) {
    setEsiTenantIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  function selectAllEsiTenants() {
    setEsiTenantIds(tenants.map((t) => t.id))
  }

  function clearAllEsiTenants() {
    setEsiTenantIds([])
  }

  function resetEsiForm() {
    setEsiName("")
    setEsiDescription("")
    setEsiStatus("Active")
    setEsiEmploymentTypes(["Permanent"])
    setEsiEmploymentTypeSearch("")
    setEsiCountry("India")
    setEsiEffectiveDate(new Date().toISOString().slice(0, 10))
    setEsiMinAge(null)
    setEsiMaxAge(null)
    setEsiTenantIds([])
    setEsiTenantSearch("")
    setEsiErrors({})
    // default formula grid (UI only)
    setEsiFormulas([
      { id: uid("esi"), account: "ESI Employee", monthlyFormula: "" },
      { id: uid("esi"), account: "ESI Employer", monthlyFormula: "" },
    ])
  }

  function openNewEsi() {
    setEditingEsiId(null)
    resetEsiForm()
    setIsEsiFormOpen(true)
    queueMicrotask(() => scrollEsiTop())
  }

  function openEditEsi(row: EsiRule) {
    setEditingEsiId(row.id)
    setEsiName(row.name)
    setEsiDescription(row.description)
    setEsiStatus(row.status)
    setEsiEmploymentTypes(row.employmentTypes.slice())
    setEsiEmploymentTypeSearch("")
    setEsiCountry(row.country)
    setEsiEffectiveDate(row.effectiveDate)
    setEsiMinAge(row.minAge)
    setEsiMaxAge(row.maxAge)
    setEsiTenantIds(row.tenantIds.slice())
    setEsiTenantSearch("")
    setEsiErrors({})
    setEsiFormulas(row.formulas.map((r) => ({ ...r, id: uid("esi") })))
    setIsEsiFormOpen(true)
    queueMicrotask(() => scrollEsiTop())
  }

  function duplicateEsi(row: EsiRule) {
    setEditingEsiId(null)
    setEsiName(getDuplicateEsiName(row.name))
    setEsiDescription(row.description)
    setEsiStatus(row.status)
    setEsiEmploymentTypes(row.employmentTypes.slice())
    setEsiEmploymentTypeSearch("")
    setEsiCountry(row.country)
    setEsiEffectiveDate(row.effectiveDate)
    setEsiMinAge(row.minAge)
    setEsiMaxAge(row.maxAge)
    setEsiTenantIds(row.tenantIds.slice())
    setEsiTenantSearch("")
    setEsiErrors({})
    setEsiFormulas(row.formulas.map((r) => ({ ...r, id: uid("esi") })))
    setIsEsiFormOpen(true)
    queueMicrotask(() => scrollEsiTop())
  }

  function validateEsi() {
    const next: Record<string, string> = {}
    if (!esiName.trim()) next.esiName = "Name is required."
    if (esiEmploymentTypes.length === 0) next.esiEmploymentTypes = "Select at least one employment type."
    if (!esiCountry.trim()) next.esiCountry = "Country is required."
    if (!esiEffectiveDate) next.esiEffectiveDate = "Effective date is required."
    if (esiMinAge !== null && esiMinAge < 0) next.esiAge = "Min age cannot be negative."
    if (esiMaxAge !== null && esiMaxAge < 0) next.esiAge = "Max age cannot be negative."
    if (esiMinAge !== null && esiMaxAge !== null && esiMaxAge < esiMinAge) next.esiAge = "Max age must be ≥ Min age."
    if (esiFormulas.length === 0) next.esiFormulas = "ESI formula grid is required."

    setEsiErrors(next)
    return Object.keys(next).length === 0
  }

  function saveEsi() {
    ;(async () => {
      if (!validateEsi()) return
      setEsiSaving(true)
      setEsiApiError(null)
      try {
        const payload = {
          name: esiName.trim(),
          description: esiDescription.trim(),
          status: esiStatus === "Inactive" ? "inactive" : "active",
          country: esiCountry.trim(),
          effectiveDate: esiEffectiveDate,
          minAge: esiMinAge,
          maxAge: esiMaxAge,
          employmentTypes: esiEmploymentTypes,
          tenantIds: esiTenantIds,
          formulas: esiFormulas.map((r) => ({
            account: r.account,
            monthlyFormula: r.monthlyFormula,
          })),
        }

        if (editingEsiId) {
          const res = await fetch(`${API_BASE}/client-esicalculation/${editingEsiId}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          })
          const json = await res.json()
          if (!res.ok) throw new Error(json?.message || "Failed to update ESI calculation.")
        } else {
          const res = await fetch(`${API_BASE}/client-esicalculation`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          })
          const json = await res.json()
          if (!res.ok) throw new Error(json?.message || "Failed to create ESI calculation.")
        }

        setIsEsiFormOpen(false)
        setEditingEsiId(null)
        setEsiSearch("")
        setEsiPage(1)
        await loadEsiCalculations({ page: 1, limit: esiPageSize, q: "" })
        queueMicrotask(() => scrollEsiTop())
      } catch (e: any) {
        const message = e?.message || "Failed to save."
        setEsiApiError(message)
        const lower = String(message).toLowerCase()
        const isDuplicate = lower.includes("duplicate") || lower.includes("already exists") || lower.includes("conflict")
        toast({
          title: isDuplicate ? "Duplicate record" : "Save failed",
          description: isDuplicate ? "A record with the same name already exists." : message,
          variant: "destructive",
        })
      } finally {
        setEsiSaving(false)
      }
    })()
  }

  function getDuplicateName(baseName: string) {
    const trimmed = baseName.trim() || "Untitled"
    const existing = new Set(methods.map((m) => m.name.trim().toLowerCase()))
    const first = `${trimmed} (Copy)`
    if (!existing.has(first.toLowerCase())) return first
    for (let i = 2; i <= 50; i++) {
      const candidate = `${trimmed} (Copy ${i})`
      if (!existing.has(candidate.toLowerCase())) return candidate
    }
    return `${trimmed} (Copy ${Date.now()})`
  }

  function apiIncomeTaxToUi(row: any): IncomeTaxMethod {
    const rawRegime = String(row?.taxRegime ?? row?.tax_regime ?? "").toLowerCase()
    const rawStatus = String(row?.status ?? "").toLowerCase()
    const effectiveRaw = String(row?.effectiveDate ?? row?.effective_date ?? "")
    // Backend returns DATE as timestamp in UTC (e.g. 2025-03-31T18:30:00.000Z) for IST midnight.
    // Convert to local yyyy-MM-dd so UI shows the correct calendar day.
    const effective = toLocalYmd(effectiveRaw)
    const updatedOn = String(
      row?.updatedAt ??
        row?.updated_at ??
        row?.updatedOn ??
        row?.updated_on ??
        row?.createdAt ??
        row?.created_at ??
        ""
    )

    const slabs = Array.isArray(row?.slabs) ? row.slabs : []
    const surcharges = Array.isArray(row?.surcharges) ? row.surcharges : []

    return {
      id: Number(row?.id),
      name: String(row?.name ?? ""),
      description: String(row?.description ?? ""),
      employmentTypes: Array.isArray(row?.employmentTypes ?? row?.employment_types)
        ? (row?.employmentTypes ?? row?.employment_types)
        : [],
      taxRegime: rawRegime === "old" ? "Old" : "New",
      country: String(row?.country ?? "India"),
      effectiveDate: effective || new Date().toISOString().slice(0, 10),
      updatedOn: updatedOn || new Date().toISOString(),
      status: rawStatus === "inactive" ? "Inactive" : "Active",
      standardDeduction: Number(row?.standardDeduction ?? row?.standard_deduction ?? 0) || 0,
      minAge: row?.minAge ?? row?.min_age ?? null,
      maxAge: row?.maxAge ?? row?.max_age ?? null,
      tenantIds: Array.isArray(row?.tenantIds ?? row?.tenant_ids) ? (row?.tenantIds ?? row?.tenant_ids) : [],
      slabs: slabs.map((s: any) => ({
        id: uid("slab"),
        from: Number(s?.from ?? 0),
        to: s?.to === null || s?.to === undefined || s?.to === "" ? null : Number(s.to),
        ratePercent: Number(s?.ratePercent ?? s?.rate_percent ?? 0),
      })),
      surcharges: surcharges.map((s: any) => ({
        id: uid("sur"),
        from: Number(s?.from ?? 0),
        to: s?.to === null || s?.to === undefined || s?.to === "" ? null : Number(s.to),
        ratePercent: Number(s?.ratePercent ?? s?.rate_percent ?? 0),
      })),
      educationCessPercent: Number(row?.educationCessPercent ?? row?.education_cess_percent ?? 0),
    }
  }

  async function loadIncomeTaxMethods(opts?: { page?: number; limit?: number; q?: string }) {
    const page = opts?.page ?? methodsPage
    const limit = opts?.limit ?? methodsPageSize
    const q = (opts?.q ?? methodsSearch).trim()

    setMethodsLoading(true)
    setMethodsApiError(null)
    try {
      const params = new URLSearchParams()
      params.set("page", String(page))
      params.set("limit", String(limit))
      if (q) params.set("q", q)

      const res = await fetch(`${API_BASE}/client-incometax?${params.toString()}`)
      const json = await res.json()
      if (!res.ok) throw new Error(json?.message || "Failed to load income tax methods.")

      const data = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : []
      const meta = json?.meta || {}

      const total =
        Number(meta?.total ?? meta?.totalRecords ?? meta?.total_records ?? data.length) || data.length
      const last =
        Number(meta?.lastPage ?? meta?.last_page ?? meta?.last_page_number ?? 1) ||
        Math.max(1, Math.ceil(total / Math.max(1, limit)))

      setMethods(data.map(apiIncomeTaxToUi))
      setMethodsTotal(total)
      setMethodsTotalPages(last)
      // keep page in range (server might clamp)
      setMethodsPage(Math.min(Math.max(1, page), last))
    } catch (e: any) {
      setMethods([])
      setMethodsTotal(0)
      setMethodsTotalPages(1)
      setMethodsApiError(e?.message || "Failed to load income tax methods.")
      toast({
        title: "Failed to load",
        description: e?.message || "Failed to load income tax methods.",
        variant: "destructive",
      })
    } finally {
      setMethodsLoading(false)
    }
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch(`${API_BASE}/tenants?page=1&limit=200`)
        const json = await res.json()
        const data: Array<{ id: number; name: string }> = Array.isArray(json?.data)
          ? json.data
          : Array.isArray(json)
            ? json
            : []
        if (cancelled) return
        setTenants(
          data
            .filter((t) => typeof t?.id === "number")
            .map((t) => ({ id: t.id, name: t.name || `Tenant ${t.id}` }))
        )
      } catch {
        // ignore (form still usable without tenant list)
      }

      try {
        const res = await fetch(`${API_BASE}/employment-types/all`)
        const json = await res.json()
        const list: string[] = Array.isArray(json?.data) ? json.data : []
        if (cancelled) return
        const unique = Array.from(
          new Set(list.map((x) => String(x || "").trim()).filter(Boolean))
        )
        if (unique.length > 0) setEmploymentTypeOptions(unique)
      } catch {
        // ignore (fallback list stays)
      }

      // Public: unique state names for PT tab
      try {
        const res = await fetch(`${API_BASE}/tenant-states/all`)
        const json = await res.json()
        const list: string[] = Array.isArray(json?.data) ? json.data : []
        if (cancelled) return
        const unique = Array.from(new Set(list.map((x) => String(x || "").trim()).filter(Boolean)))
        setPtStates(unique)
      } catch (e: any) {
        toast({
          title: "PT states not available",
          description:
            e?.message ||
            "Could not load tenant states. Ensure backend has the public `/tenant-states/all` route and restart it.",
          variant: "destructive",
        })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [API_BASE])

  const filteredPtStates = useMemo(() => {
    const q = ptStateSearch.trim().toLowerCase()
    if (!q) return ptStates
    return ptStates.filter((s) => s.toLowerCase().includes(q))
  }, [ptStates, ptStateSearch])

  const filteredLwfStates = useMemo(() => {
    const q = lwfStateSearch.trim().toLowerCase()
    if (!q) return ptStates
    return ptStates.filter((s) => s.toLowerCase().includes(q))
  }, [ptStates, lwfStateSearch])

  const filteredPtEmploymentTypes = useMemo(() => {
    const q = ptEmploymentTypeSearch.trim().toLowerCase()
    if (!q) return employmentTypeOptions
    return employmentTypeOptions.filter((t) => t.toLowerCase().includes(q))
  }, [employmentTypeOptions, ptEmploymentTypeSearch])

  const filteredLwfEmploymentTypes = useMemo(() => {
    const q = lwfEmploymentTypeSearch.trim().toLowerCase()
    if (!q) return employmentTypeOptions
    return employmentTypeOptions.filter((t) => t.toLowerCase().includes(q))
  }, [employmentTypeOptions, lwfEmploymentTypeSearch])

  const filteredPtTenants = useMemo(() => {
    const q = ptTenantSearch.trim().toLowerCase()
    if (!q) return tenants
    return tenants.filter((t) => `${t.id} ${t.name}`.toLowerCase().includes(q))
  }, [ptTenantSearch, tenants])

  const filteredLwfTenants = useMemo(() => {
    const q = lwfTenantSearch.trim().toLowerCase()
    if (!q) return tenants
    return tenants.filter((t) => `${t.id} ${t.name}`.toLowerCase().includes(q))
  }, [lwfTenantSearch, tenants])

  async function loadLwfCalculations(opts?: { page?: number; limit?: number; q?: string }) {
    const page = opts?.page ?? lwfPage
    const limit = opts?.limit ?? lwfPageSize
    const q = (opts?.q ?? lwfSearch).trim()

    setLwfLoading(true)
    setLwfApiError(null)
    try {
      const params = new URLSearchParams()
      params.set("page", String(page))
      params.set("limit", String(limit))
      if (q) params.set("q", q)

      const res = await fetch(`${API_BASE}/client-lwfcalculation?${params.toString()}`)
      const json = await res.json()
      if (!res.ok) throw new Error(json?.message || "Failed to load LWF calculations.")

      const data = Array.isArray(json?.data) ? json.data : []
      const meta = json?.meta || {}
      const total = Number(meta?.total ?? data.length) || data.length
      const last = Number(meta?.lastPage ?? 1) || 1

      setLwfRules(
        data.map((row: any) => ({
          id: Number(row?.id),
          stateName: String(row?.stateName ?? row?.state_name ?? ""),
          country: String(row?.country ?? "India"),
          effectiveFrom: String(row?.effectiveFrom ?? row?.effective_from ?? "").slice(0, 10),
          updatedOn: String(row?.updatedAt ?? row?.updated_at ?? row?.createdAt ?? row?.created_at ?? ""),
          frequency:
            String(row?.frequency ?? "monthly").toLowerCase() === "quarterly"
              ? "Quarterly"
              : String(row?.frequency ?? "").toLowerCase() === "half-yearly"
                ? "Half-yearly"
                : "Monthly",
          minAge: row?.minAge ?? row?.min_age ?? null,
          maxAge: row?.maxAge ?? row?.max_age ?? null,
          employmentTypes: Array.isArray(row?.employmentTypes ?? row?.employment_types)
            ? (row?.employmentTypes ?? row?.employment_types)
            : [],
          tenantIds: Array.isArray(row?.tenantIds ?? row?.tenant_ids) ? (row?.tenantIds ?? row?.tenant_ids) : [],
          slabs: Array.isArray(row?.slabs)
            ? row.slabs.map((s: any) => ({
                id: uid("lwf"),
                from: Number(s?.from ?? 0),
                to: s?.to === null || s?.to === undefined || s?.to === "" ? null : Number(s.to),
                employeeAmount: Number(
                  s?.employeeAmount ?? s?.employee_amount ?? s?.employee ?? s?.amount ?? 0
                ),
                employerAmount: Number(
                  s?.employerAmount ?? s?.employer_amount ?? s?.employer ?? 0
                ),
              }))
            : [],
        }))
      )
      setLwfPage(Math.min(Math.max(1, page), last))
      setLwfTotal(total)
      setLwfTotalPages(last)
    } catch (e: any) {
      setLwfRules([])
      setLwfApiError(e?.message || "Failed to load LWF calculations.")
      toast({
        title: "Failed to load",
        description: e?.message || "Failed to load LWF calculations.",
        variant: "destructive",
      })
    } finally {
      setLwfLoading(false)
    }
  }

  useEffect(() => {
    setLwfPage(1)
  }, [lwfSearch, lwfPageSize])

  useEffect(() => {
    loadLwfCalculations({ page: lwfPage, limit: lwfPageSize, q: lwfSearch })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE, lwfPage, lwfPageSize, lwfSearch])

  async function loadPtCalculations(opts?: { page?: number; limit?: number; q?: string }) {
    const page = opts?.page ?? ptPage
    const limit = opts?.limit ?? ptPageSize
    const q = (opts?.q ?? ptSearch).trim()

    setPtLoading(true)
    setPtApiError(null)
    try {
      const params = new URLSearchParams()
      params.set("page", String(page))
      params.set("limit", String(limit))
      if (q) params.set("q", q)
      const res = await fetch(`${API_BASE}/client-ptcalculation?${params.toString()}`)
      const json = await res.json()
      if (!res.ok) throw new Error(json?.message || "Failed to load PT calculations.")

      const data = Array.isArray(json?.data) ? json.data : []
      const meta = json?.meta || {}
      const total = Number(meta?.total ?? data.length) || data.length
      const last = Number(meta?.lastPage ?? 1) || 1

      setPtRules(
        data.map((row: any) => ({
          id: Number(row?.id),
          stateName: String(row?.stateName ?? row?.state_name ?? ""),
          country: String(row?.country ?? "India"),
          effectiveFrom: String(row?.effectiveFrom ?? row?.effective_from ?? "").slice(0, 10),
          updatedOn: String(row?.updatedAt ?? row?.updated_at ?? row?.createdAt ?? row?.created_at ?? ""),
          frequency:
            String(row?.frequency ?? "monthly").toLowerCase() === "quarterly"
              ? "Quarterly"
              : String(row?.frequency ?? "").toLowerCase() === "half-yearly"
                ? "Half-yearly"
                : "Monthly",
          minAge: row?.minAge ?? row?.min_age ?? null,
          maxAge: row?.maxAge ?? row?.max_age ?? null,
          employmentTypes: Array.isArray(row?.employmentTypes ?? row?.employment_types)
            ? (row?.employmentTypes ?? row?.employment_types)
            : [],
          tenantIds: Array.isArray(row?.tenantIds ?? row?.tenant_ids) ? (row?.tenantIds ?? row?.tenant_ids) : [],
          slabs: Array.isArray(row?.slabs)
            ? row.slabs.map((s: any) => ({
                id: uid("pt"),
                from: Number(s?.from ?? 0),
                to: s?.to === null || s?.to === undefined || s?.to === "" ? null : Number(s.to),
                amount: Number(s?.amount ?? 0),
              }))
            : [],
        }))
      )
      setPtPage(Math.min(Math.max(1, page), last))
      setPtTotal(total)
      setPtTotalPages(last)
    } catch (e: any) {
      setPtRules([])
      setPtApiError(e?.message || "Failed to load PT calculations.")
      toast({ title: "Failed to load", description: e?.message || "Failed to load PT calculations.", variant: "destructive" })
    } finally {
      setPtLoading(false)
    }
  }

  useEffect(() => {
    setPtPage(1)
  }, [ptSearch, ptPageSize])

  useEffect(() => {
    loadPtCalculations({ page: ptPage, limit: ptPageSize, q: ptSearch })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE, ptPage, ptPageSize, ptSearch])

  function resetPtForm() {
    setPtStateName("")
    setPtStateSearch("")
    setPtEmploymentTypes(["Permanent"])
    setPtEmploymentTypeSearch("")
    setPtCountry("India")
    setPtEffectiveFrom(new Date().toISOString().slice(0, 10))
    setPtFrequency("Monthly")
    setPtMinAge(null)
    setPtMaxAge(null)
    setPtTenantIds([])
    setPtTenantSearch("")
    setPtSlabs([{ id: uid("pt"), from: 0, to: null, amount: 0 }])
  }

  function resetLwfForm() {
    setLwfStateName("")
    setLwfStateSearch("")
    setLwfEmploymentTypes(["Permanent"])
    setLwfEmploymentTypeSearch("")
    setLwfCountry("India")
    setLwfEffectiveFrom(new Date().toISOString().slice(0, 10))
    setLwfFrequency("Monthly")
    setLwfMinAge(null)
    setLwfMaxAge(null)
    setLwfTenantIds([])
    setLwfTenantSearch("")
    setLwfSlabs([{ id: uid("lwf"), from: 0, to: null, employeeAmount: 0, employerAmount: 0 }])
  }

  function openNewLwf() {
    setEditingLwfId(null)
    resetLwfForm()
    setIsLwfFormOpen(true)
  }

  function openEditLwf(r: LwfRule) {
    setEditingLwfId(r.id)
    setLwfStateName(r.stateName)
    setLwfStateSearch("")
    setLwfEmploymentTypes(r.employmentTypes.slice())
    setLwfEmploymentTypeSearch("")
    setLwfCountry(r.country)
    setLwfEffectiveFrom(r.effectiveFrom)
    setLwfFrequency(r.frequency)
    setLwfMinAge(r.minAge)
    setLwfMaxAge(r.maxAge)
    setLwfTenantIds(r.tenantIds.slice())
    setLwfTenantSearch("")
    setLwfSlabs(
      r.slabs.map((s) => ({
        ...s,
        id: uid("lwf"),
        employeeAmount: Number((s as any)?.employeeAmount ?? (s as any)?.employee ?? (s as any)?.amount ?? 0),
        employerAmount: Number((s as any)?.employerAmount ?? (s as any)?.employer ?? 0),
      }))
    )
    setIsLwfFormOpen(true)
  }

  function toggleLwfEmploymentType(v: EmploymentType) {
    setLwfEmploymentTypes((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]))
  }

  function selectAllLwfEmploymentTypes() {
    setLwfEmploymentTypes(employmentTypeOptions.slice())
  }

  function clearAllLwfEmploymentTypes() {
    setLwfEmploymentTypes([])
  }

  function toggleLwfTenant(id: number) {
    setLwfTenantIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  function selectAllLwfTenants() {
    setLwfTenantIds(tenants.map((t) => t.id))
  }

  function clearAllLwfTenants() {
    setLwfTenantIds([])
  }

  function saveLwf() {
    if (!lwfStateName.trim()) {
      toast({ title: "Validation", description: "State is required.", variant: "destructive" })
      return
    }
    if (lwfEmploymentTypes.length === 0) {
      toast({
        title: "Validation",
        description: "Select at least one employment type.",
        variant: "destructive",
      })
      return
    }
    if (!lwfCountry.trim()) {
      toast({ title: "Validation", description: "Country is required.", variant: "destructive" })
      return
    }
    if (lwfMinAge !== null && lwfMinAge < 0) {
      toast({ title: "Validation", description: "Min age cannot be negative.", variant: "destructive" })
      return
    }
    if (lwfMaxAge !== null && lwfMaxAge < 0) {
      toast({ title: "Validation", description: "Max age cannot be negative.", variant: "destructive" })
      return
    }
    if (lwfMinAge !== null && lwfMaxAge !== null && lwfMaxAge < lwfMinAge) {
      toast({ title: "Validation", description: "Max age must be ≥ Min age.", variant: "destructive" })
      return
    }

    const nowIso = new Date().toISOString()
    const record: Omit<LwfRule, "id"> = {
      stateName: lwfStateName.trim(),
      country: lwfCountry.trim(),
      effectiveFrom: lwfEffectiveFrom,
      updatedOn: nowIso,
      frequency: lwfFrequency,
      minAge: lwfMinAge,
      maxAge: lwfMaxAge,
      employmentTypes: lwfEmploymentTypes,
      tenantIds: lwfTenantIds,
      slabs: lwfSlabs.map((s) => ({ ...s })),
    }

    ;(async () => {
      setLwfSaving(true)
      setLwfApiError(null)
      const payload = {
        stateName: record.stateName,
        country: record.country,
        frequency: record.frequency.toLowerCase(),
        effectiveFrom: record.effectiveFrom,
        minAge: record.minAge,
        maxAge: record.maxAge,
        employmentTypes: record.employmentTypes,
        tenantIds: record.tenantIds,
        slabs: record.slabs.map((s) => ({
          from: s.from,
          to: s.to,
          employeeAmount: s.employeeAmount,
          employerAmount: s.employerAmount,
        })),
      }
      try {
        if (editingLwfId) {
          const res = await fetch(`${API_BASE}/client-lwfcalculation/${editingLwfId}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          })
          const json = await res.json()
          if (!res.ok) throw new Error(json?.message || "Failed to update LWF calculation.")
        } else {
          const res = await fetch(`${API_BASE}/client-lwfcalculation`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          })
          const json = await res.json()
          if (!res.ok) throw new Error(json?.message || "Failed to create LWF calculation.")
        }

        setIsLwfFormOpen(false)
        setEditingLwfId(null)
        setLwfSearch("")
        setLwfPage(1)
        await loadLwfCalculations({ page: 1, limit: lwfPageSize, q: "" })
      } catch (e: any) {
        const message = e?.message || "Failed to save."
        setLwfApiError(message)
        const lower = String(message).toLowerCase()
        const isDuplicate = lower.includes("duplicate") || lower.includes("already exists") || lower.includes("conflict")
        toast({
          title: isDuplicate ? "Duplicate record" : "Save failed",
          description: isDuplicate ? "A record with the same state and effective date already exists." : message,
          variant: "destructive",
        })
      } finally {
        setLwfSaving(false)
      }
    })()
  }

  function openNewPt() {
    setEditingPtId(null)
    resetPtForm()
    setIsPtFormOpen(true)
  }

  function openEditPt(r: PtRule) {
    setEditingPtId(r.id)
    setPtStateName(r.stateName)
    setPtStateSearch("")
    setPtEmploymentTypes(r.employmentTypes.slice())
    setPtEmploymentTypeSearch("")
    setPtCountry(r.country)
    setPtEffectiveFrom(r.effectiveFrom)
    setPtFrequency(r.frequency)
    setPtMinAge(r.minAge)
    setPtMaxAge(r.maxAge)
    setPtTenantIds(r.tenantIds.slice())
    setPtTenantSearch("")
    setPtSlabs(r.slabs.map((s) => ({ ...s, id: uid("pt") })))
    setIsPtFormOpen(true)
  }

  function togglePtEmploymentType(v: EmploymentType) {
    setPtEmploymentTypes((prev) =>
      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]
    )
  }

  function selectAllPtEmploymentTypes() {
    setPtEmploymentTypes(employmentTypeOptions.slice())
  }

  function clearAllPtEmploymentTypes() {
    setPtEmploymentTypes([])
  }

  function togglePtTenant(id: number) {
    setPtTenantIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  function selectAllPtTenants() {
    setPtTenantIds(tenants.map((t) => t.id))
  }

  function clearAllPtTenants() {
    setPtTenantIds([])
  }

  function savePt() {
    if (!ptStateName.trim()) {
      toast({ title: "Validation", description: "State is required.", variant: "destructive" })
      return
    }
    if (ptEmploymentTypes.length === 0) {
      toast({
        title: "Validation",
        description: "Select at least one employment type.",
        variant: "destructive",
      })
      return
    }
    if (!ptCountry.trim()) {
      toast({ title: "Validation", description: "Country is required.", variant: "destructive" })
      return
    }
    if (ptMinAge !== null && ptMinAge < 0) {
      toast({ title: "Validation", description: "Min age cannot be negative.", variant: "destructive" })
      return
    }
    if (ptMaxAge !== null && ptMaxAge < 0) {
      toast({ title: "Validation", description: "Max age cannot be negative.", variant: "destructive" })
      return
    }
    if (ptMinAge !== null && ptMaxAge !== null && ptMaxAge < ptMinAge) {
      toast({ title: "Validation", description: "Max age must be ≥ Min age.", variant: "destructive" })
      return
    }
    const nowIso = new Date().toISOString()
    const record: Omit<PtRule, "id"> = {
      stateName: ptStateName.trim(),
      country: ptCountry.trim(),
      effectiveFrom: ptEffectiveFrom,
      updatedOn: nowIso,
      frequency: ptFrequency,
      minAge: ptMinAge,
      maxAge: ptMaxAge,
      employmentTypes: ptEmploymentTypes,
      tenantIds: ptTenantIds,
      slabs: ptSlabs.map((s) => ({ ...s })),
    }

    ;(async () => {
      setPtSaving(true)
      setPtApiError(null)
      const payload = {
        stateName: record.stateName,
        country: record.country,
        frequency: record.frequency.toLowerCase(),
        effectiveFrom: record.effectiveFrom,
        minAge: record.minAge,
        maxAge: record.maxAge,
        employmentTypes: record.employmentTypes,
        tenantIds: record.tenantIds,
        slabs: record.slabs.map((s) => ({ from: s.from, to: s.to, amount: s.amount })),
      }
      try {
        if (editingPtId) {
          const res = await fetch(`${API_BASE}/client-ptcalculation/${editingPtId}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          })
          const json = await res.json()
          if (!res.ok) throw new Error(json?.message || "Failed to update PT calculation.")
        } else {
          const res = await fetch(`${API_BASE}/client-ptcalculation`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          })
          const json = await res.json()
          if (!res.ok) throw new Error(json?.message || "Failed to create PT calculation.")
        }

        setIsPtFormOpen(false)
        setEditingPtId(null)
        setPtSearch("")
        setPtPage(1)
        await loadPtCalculations({ page: 1, limit: ptPageSize, q: "" })
      } catch (e: any) {
        const message = e?.message || "Failed to save."
        setPtApiError(message)
        const lower = String(message).toLowerCase()
        const isDuplicate = lower.includes("duplicate") || lower.includes("already exists") || lower.includes("conflict")
        toast({
          title: isDuplicate ? "Duplicate record" : "Save failed",
          description: isDuplicate ? "A record with the same state and effective date already exists." : message,
          variant: "destructive",
        })
      } finally {
        setPtSaving(false)
      }
    })()
  }

  useEffect(() => {
    // reset to first page when filtering/page-size changes
    setMethodsPage(1)
  }, [methodsSearch, methodsPageSize])

  useEffect(() => {
    // load from DB (server-side pagination + search)
    loadIncomeTaxMethods({ page: methodsPage, limit: methodsPageSize, q: methodsSearch })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE, methodsPage, methodsPageSize, methodsSearch])

  const filteredTenants = useMemo(() => {
    const q = tenantSearch.trim().toLowerCase()
    if (!q) return tenants
    return tenants.filter((t) => `${t.id} ${t.name}`.toLowerCase().includes(q))
  }, [tenantSearch, tenants])

  const filteredEmploymentTypes = useMemo(() => {
    const q = employmentTypeSearch.trim().toLowerCase()
    if (!q) return employmentTypeOptions
    return employmentTypeOptions.filter((t) => t.toLowerCase().includes(q))
  }, [employmentTypeOptions, employmentTypeSearch])

  function toggleEmploymentType(v: EmploymentType) {
    setEmploymentTypes((prev) =>
      prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]
    )
  }

  function toggleTenant(id: number) {
    setSelectedTenantIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }

  function selectAllTenants() {
    setSelectedTenantIds(tenants.map((t) => t.id))
  }

  function clearAllTenants() {
    setSelectedTenantIds([])
  }

  function selectAllEmploymentTypes() {
    setEmploymentTypes(employmentTypeOptions.slice())
  }

  function clearAllEmploymentTypes() {
    setEmploymentTypes([])
  }

  function validate() {
    const next: Record<string, string> = {}

    if (!name.trim()) next.name = "Name is required."
    if (employmentTypes.length === 0) next.employmentTypes = "Select at least one employment type."
    if (!country.trim()) next.country = "Country is required."
    if (!effectiveDate) next.effectiveDate = "Effective date is required."
    {
      const v = standardDeduction.trim()
      const n = v === "" ? 0 : Number.parseFloat(v)
      if (!Number.isFinite(n) || n < 0) next.standardDeduction = "Standard Deduction must be a number ≥ 0."
    }
    if (minAge !== null && minAge < 0) next.age = "Min age cannot be negative."
    if (maxAge !== null && maxAge < 0) next.age = "Max age cannot be negative."
    if (minAge !== null && maxAge !== null && maxAge < minAge) next.age = "Max age must be ≥ Min age."

    if (slabs.length === 0) next.slabs = "Add at least one slab."
    for (const s of slabs) {
      if (s.from < 0) next.slabs = "Slab 'from' cannot be negative."
      if (s.to !== null && s.to < s.from) next.slabs = "Slab 'to' must be >= 'from'."
      if (s.ratePercent < 0) next.slabs = "Slab rate cannot be negative."
    }

    for (const s of surcharges) {
      if (s.from < 0) next.surcharges = "Surcharge 'from' cannot be negative."
      if (s.to !== null && s.to < s.from) next.surcharges = "Surcharge 'to' must be >= 'from'."
      if (s.ratePercent < 0) next.surcharges = "Surcharge rate cannot be negative."
    }
    if (educationCessPercent < 0) next.educationCessPercent = "Education Cess cannot be negative."

    setErrors(next)
    return Object.keys(next).length === 0
  }

  function openNewIncomeTaxMethod() {
    setEditingMethodId(null)
    resetForm()
    setIsIncomeTaxFormOpen(true)
    queueMicrotask(() => nameInputRef.current?.focus())
  }

  function scrollIncomeTaxTop() {
    incomeTaxTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  function openEditIncomeTaxMethod(m: IncomeTaxMethod) {
    setEditingMethodId(m.id)
    setName(m.name)
    setDescription(m.description)
    setEmploymentTypes(m.employmentTypes.slice())
    setTaxRegime(m.taxRegime)
    setCountry(m.country)
    setEffectiveDate(m.effectiveDate)
    setStandardDeduction(String(m.standardDeduction ?? 0))
    setMinAge(m.minAge)
    setMaxAge(m.maxAge)
    setSelectedTenantIds(m.tenantIds.slice())
    setTenantSearch("")
    setEmploymentTypeSearch("")
    // clone with fresh row ids to avoid collisions inside editable tables
    setSlabs(m.slabs.map((x) => ({ ...x, id: uid("slab") })))
    setSurcharges(m.surcharges.map((x) => ({ ...x, id: uid("sur") })))
    setEducationCessPercent(m.educationCessPercent)
    setErrors({})
    setIsIncomeTaxFormOpen(true)
    queueMicrotask(() => nameInputRef.current?.focus())
  }

  function openDuplicateIncomeTaxMethod(m: IncomeTaxMethod) {
    setEditingMethodId(null)
    setName(getDuplicateName(m.name))
    setDescription(m.description)
    setEmploymentTypes(m.employmentTypes.slice())
    setTaxRegime(m.taxRegime)
    setCountry(m.country)
    setEffectiveDate(m.effectiveDate)
    setStandardDeduction(String(m.standardDeduction ?? 0))
    setMinAge(m.minAge)
    setMaxAge(m.maxAge)
    setSelectedTenantIds(m.tenantIds.slice())
    setTenantSearch("")
    setEmploymentTypeSearch("")
    setSlabs(m.slabs.map((x) => ({ ...x, id: uid("slab") })))
    setSurcharges(m.surcharges.map((x) => ({ ...x, id: uid("sur") })))
    setEducationCessPercent(m.educationCessPercent)
    setErrors({})
    setIsIncomeTaxFormOpen(true)
    queueMicrotask(() => nameInputRef.current?.focus())
  }

  function resetForm() {
    setName("")
    setDescription("")
    setEmploymentTypes(["Permanent"])
    setTaxRegime("New")
    setCountry("India")
    setEffectiveDate(new Date().toISOString().slice(0, 10))
    setStandardDeduction("0")
    setMinAge(null)
    setMaxAge(null)
    setSelectedTenantIds([])
    setTenantSearch("")
    setEmploymentTypeSearch("")
    setSlabs([
      { id: uid("slab"), from: 0, to: 250000, ratePercent: 0 },
      { id: uid("slab"), from: 250001, to: 500000, ratePercent: 5 },
    ])
    setSurcharges([{ id: uid("sur"), from: 5000000, to: null, ratePercent: 10 }])
    setEducationCessPercent(4)
    setErrors({})
  }

  function saveMethod() {
    ;(async () => {
      if (!validate()) return

      setMethodsSaving(true)
      setMethodsApiError(null)

      const payload = {
        name: name.trim(),
        description: description.trim(),
        employmentTypes,
        taxRegime: taxRegime === "Old" ? "old" : "new",
        country: country.trim(),
        effectiveDate,
        standardDeduction:
          standardDeduction.trim() === "" ? 0 : Number.parseFloat(standardDeduction),
        minAge,
        maxAge,
        tenantIds: selectedTenantIds,
        slabs: slabs.map((s) => ({ from: s.from, to: s.to, ratePercent: s.ratePercent })),
        surcharges: surcharges.map((s) => ({ from: s.from, to: s.to, ratePercent: s.ratePercent })),
        educationCessPercent,
      }

      try {
        if (editingMethodId) {
          const res = await fetch(`${API_BASE}/client-incometax/${editingMethodId}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          })
          const json = await res.json()
          if (!res.ok) throw new Error(json?.message || "Failed to update income tax method.")
        } else {
          const res = await fetch(`${API_BASE}/client-incometax`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          })
          const json = await res.json()
          if (!res.ok) throw new Error(json?.message || "Failed to create income tax method.")
        }

        resetForm()
        setIsIncomeTaxFormOpen(false)
        setEditingMethodId(null)
        setMethodsPage(1)
        await loadIncomeTaxMethods({ page: 1, limit: methodsPageSize, q: methodsSearch })
        queueMicrotask(() => scrollIncomeTaxTop())
      } catch (e: any) {
        const message = e?.message || "Failed to save."
        setMethodsApiError(message)
        const lower = String(message).toLowerCase()
        const isDuplicate =
          lower.includes("duplicate") ||
          lower.includes("already exists") ||
          lower.includes("conflict")
        toast({
          title: isDuplicate ? "Duplicate record" : "Save failed",
          description: isDuplicate ? "A record with the same name already exists." : message,
          variant: "destructive",
        })
      } finally {
        setMethodsSaving(false)
      }
    })()
  }

  return (
    <>
      <SEO
        title="Payroll Settings"
        description="Internal payroll configuration console."
      />

      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Payroll Settings</h1>
          <p className="text-muted-foreground mt-1">
            Configure payroll rules, statutory settings, and approvals.
          </p>
        </div>

        <Card>
          <CardContent className="pt-6">
            <Tabs defaultValue="income-tax">
              <TabsList className="w-full flex flex-wrap justify-start gap-1 h-auto p-1">
                <TabsTrigger value="income-tax">Income tax</TabsTrigger>
                <TabsTrigger value="pt">PT</TabsTrigger>
                <TabsTrigger value="pf">PF</TabsTrigger>
                <TabsTrigger value="esi">ESI</TabsTrigger>
                <TabsTrigger value="lwf">LWF</TabsTrigger>
                <TabsTrigger value="gratuity">Gratuity</TabsTrigger>
                <TabsTrigger value="stat-bonus">Stat Bonus</TabsTrigger>
              </TabsList>

              <TabsContent value="income-tax">
                <div className="space-y-4">
                  <div ref={incomeTaxTopRef} />

                  {!isIncomeTaxFormOpen && (
                    <div className="rounded-lg border bg-white p-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="w-full sm:max-w-[420px]">
                          <Input
                            value={methodsSearch}
                            onChange={(e) => setMethodsSearch(e.target.value)}
                            placeholder="Search..."
                          />
                        </div>
                        <Button onClick={openNewIncomeTaxMethod}>Add New</Button>
                      </div>

                      <div className="mt-4 overflow-x-auto">
                        <table className="w-full min-w-[920px] text-sm">
                          <thead className="bg-muted/30">
                            <tr className="text-left">
                              <th className="px-3 py-2 font-semibold">Name</th>
                              <th className="px-3 py-2 font-semibold">Tax Regime</th>
                              <th className="px-3 py-2 font-semibold">Effective Date</th>
                              <th className="px-3 py-2 font-semibold">Updated on</th>
                              <th className="px-3 py-2 font-semibold">Status</th>
                              <th className="px-3 py-2 font-semibold">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {methodsLoading ? (
                              <tr className="border-t">
                                <td className="px-3 py-6 text-muted-foreground" colSpan={6}>
                                  Loading...
                                </td>
                              </tr>
                            ) : methods.length === 0 ? (
                              <tr className="border-t">
                                <td className="px-3 py-6 text-muted-foreground" colSpan={6}>
                                  {methodsApiError ? methodsApiError : "No records found."}
                                </td>
                              </tr>
                            ) : (
                              methods.map((m) => (
                                <tr key={m.id} className="border-t">
                                  <td className="px-3 py-3 font-medium">{m.name}</td>
                                  <td className="px-3 py-3">{m.taxRegime}</td>
                                  <td className="px-3 py-3">{formatDdMmmYyyy(m.effectiveDate)}</td>
                                  <td className="px-3 py-3">{formatDdMmmYyyy(m.updatedOn)}</td>
                                  <td className="px-3 py-3">
                                    <span
                                      className={
                                        m.status === "Active"
                                          ? "inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700"
                                          : "inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
                                      }
                                    >
                                      {m.status}
                                    </span>
                                  </td>
                                  <td className="px-3 py-3">
                                    <div className="flex items-center gap-1">
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        title="Edit"
                                        onClick={() => openEditIncomeTaxMethod(m)}
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        title="Duplicate"
                                        onClick={() => openDuplicateIncomeTaxMethod(m)}
                                      >
                                        <Copy className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>

                    <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div className="flex items-center gap-3">
                        <div className="text-sm text-muted-foreground">Rows per page</div>
                        <select
                          className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                          value={methodsPageSize}
                          onChange={(e) => setMethodsPageSize(Number(e.target.value))}
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </div>

                      <div className="text-sm text-muted-foreground md:text-center">
                        <span className="font-medium text-foreground">{methodsTotal}</span> record(s) total
                      </div>

                      <div className="flex items-center gap-3 md:justify-end">
                        <div className="text-sm text-muted-foreground">
                          Page <span className="font-medium text-foreground">{methodsPage}</span> of{" "}
                          <span className="font-medium text-foreground">{methodsTotalPages}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            disabled={methodsPage <= 1}
                            onClick={() => setMethodsPage(1)}
                            title="First page"
                          >
                            <ChevronsLeft className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            disabled={methodsPage <= 1}
                            onClick={() => setMethodsPage((p) => Math.max(1, p - 1))}
                            title="Previous page"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            disabled={methodsPage >= methodsTotalPages}
                            onClick={() => setMethodsPage((p) => Math.min(methodsTotalPages, p + 1))}
                            title="Next page"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            disabled={methodsPage >= methodsTotalPages}
                            onClick={() => setMethodsPage(methodsTotalPages)}
                            title="Last page"
                          >
                            <ChevronsRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    </div>
                  )}

                  {isIncomeTaxFormOpen && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="rounded-lg border bg-white p-4">
                      <div className="text-sm font-semibold">Calculation method</div>
                      <div className="text-sm text-muted-foreground mt-1">
                        Configure how tax, surcharge and education cess should be calculated.
                      </div>

                      <div className="mt-4 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <div className="text-sm font-medium">Name</div>
                            <Input
                              ref={nameInputRef}
                              value={name}
                              onChange={(e) => setName(e.target.value)}
                            />
                            {errors.name && <div className="text-xs text-red-600">{errors.name}</div>}
                          </div>
                          <div className="space-y-2">
                            <div className="text-sm font-medium">Tax Regime</div>
                            <select
                              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                              value={taxRegime}
                              onChange={(e) => setTaxRegime(e.target.value as TaxRegime)}
                            >
                              <option value="New">New</option>
                              <option value="Old">Old</option>
                            </select>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <div className="text-sm font-medium">Description</div>
                          <Textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="min-h-[90px]"
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <div className="text-sm font-medium">Employment Category (all tenants)</div>
                            <div className="rounded-md border bg-muted/20 p-3 space-y-2">
                              <Input
                                value={employmentTypeSearch}
                                onChange={(e) => setEmploymentTypeSearch(e.target.value)}
                                placeholder="Search categories..."
                              />
                              <div className="flex items-center gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={selectAllEmploymentTypes}
                                >
                                  Select all
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={clearAllEmploymentTypes}
                                >
                                  Clear all
                                </Button>
                              </div>
                              <div className="max-h-[180px] overflow-auto space-y-2 text-sm">
                                {filteredEmploymentTypes.length === 0 ? (
                                  <div className="text-muted-foreground">No categories found.</div>
                                ) : (
                                  filteredEmploymentTypes.map((t) => (
                                    <label key={t} className="flex items-center gap-2">
                                      <input
                                        type="checkbox"
                                        checked={employmentTypes.includes(t)}
                                        onChange={() => toggleEmploymentType(t)}
                                      />
                                      <span className="truncate">{t}</span>
                                    </label>
                                  ))
                                )}
                              </div>
                            </div>
                            {errors.employmentTypes && (
                              <div className="text-xs text-red-600">{errors.employmentTypes}</div>
                            )}
                          </div>

                          <div className="space-y-2">
                            <div className="text-sm font-medium">Country</div>
                            <select
                              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                              value={country}
                              onChange={(e) => setCountry(e.target.value)}
                            >
                              <option value="India">India</option>
                              <option value="United States">United States</option>
                              <option value="United Kingdom">United Kingdom</option>
                              <option value="Singapore">Singapore</option>
                              <option value="UAE">UAE</option>
                              <option value="Other">Other</option>
                            </select>
                            {errors.country && (
                              <div className="text-xs text-red-600">{errors.country}</div>
                            )}

                            <div className="pt-2">
                              <div className="text-sm font-medium">Effective date</div>
                              <DatePicker value={effectiveDate} onChange={setEffectiveDate} />
                              {errors.effectiveDate && (
                                <div className="text-xs text-red-600 mt-1">{errors.effectiveDate}</div>
                              )}
                            </div>

                            <div className="pt-2">
                              <div className="text-sm font-medium">Standard Deduction</div>
                              <Input
                                type="text"
                                inputMode="decimal"
                                placeholder="0"
                                value={standardDeduction}
                                onChange={(e) => {
                                  // numeric input without spinner
                                  const next = e.target.value.replace(/[^\d.]/g, "")
                                  const parts = next.split(".")
                                  const normalized =
                                    parts.length <= 2
                                      ? parts[0] + (parts.length === 2 ? `.${parts[1]}` : "")
                                      : parts[0] + "." + parts.slice(1).join("")
                                  setStandardDeduction(normalized)
                                }}
                              />
                              {errors.standardDeduction && (
                                <div className="text-xs text-red-600 mt-1">{errors.standardDeduction}</div>
                              )}
                            </div>

                            <div className="pt-2">
                              <div className="text-sm font-medium">Age criteria</div>
                              <div className="mt-2 grid grid-cols-2 gap-2">
                                <Input
                                  type="number"
                                  placeholder="Min age"
                                  value={minAge ?? ""}
                                  onChange={(e) =>
                                    setMinAge(e.target.value === "" ? null : Number(e.target.value))
                                  }
                                />
                                <Input
                                  type="number"
                                  placeholder="Max age"
                                  value={maxAge ?? ""}
                                  onChange={(e) =>
                                    setMaxAge(e.target.value === "" ? null : Number(e.target.value))
                                  }
                                />
                              </div>
                              {errors.age && <div className="text-xs text-red-600 mt-1">{errors.age}</div>}
                            </div>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <div className="text-sm font-medium">
                            Tenants (no selection means applicable to all tenants)
                          </div>
                          <Input
                            value={tenantSearch}
                            onChange={(e) => setTenantSearch(e.target.value)}
                            placeholder="Search tenants..."
                          />
                          <div className="flex items-center gap-2">
                            <Button type="button" variant="outline" size="sm" onClick={selectAllTenants}>
                              Select all
                            </Button>
                            <Button type="button" variant="outline" size="sm" onClick={clearAllTenants}>
                              Clear all
                            </Button>
                          </div>
                          <div className="rounded-md border bg-muted/20 p-3 max-h-[220px] overflow-auto space-y-2 text-sm">
                            {filteredTenants.length === 0 ? (
                              <div className="text-muted-foreground">No tenants found.</div>
                            ) : (
                              filteredTenants.map((t) => (
                                <label key={t.id} className="flex items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={selectedTenantIds.includes(t.id)}
                                    onChange={() => toggleTenant(t.id)}
                                  />
                                  <span className="font-medium">{t.name}</span>
                                  <span className="text-muted-foreground">#{t.id}</span>
                                </label>
                              ))
                            )}
                          </div>
                        </div>

                        <div className="flex items-center justify-end gap-2">
                          <Button variant="outline" onClick={resetForm}>
                            Reset
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => {
                              setIsIncomeTaxFormOpen(false)
                              setEditingMethodId(null)
                              resetForm()
                              queueMicrotask(() => scrollIncomeTaxTop())
                            }}
                          >
                            Close
                          </Button>
                          <Button onClick={saveMethod} disabled={methodsSaving}>
                            {methodsSaving ? "Saving..." : "Save method"}
                          </Button>
                        </div>
                        {methodsApiError && (
                          <div className="text-xs text-red-600 text-right">{methodsApiError}</div>
                        )}
                      </div>
                    </div>

                    <div className="rounded-lg border bg-white p-4">
                      <div className="text-sm font-semibold">Slabs</div>
                      <div className="text-sm text-muted-foreground mt-1">
                        Define slabs for tax, surcharge and education cess.
                      </div>

                      <div className="mt-4 space-y-4">
                        <div>
                          <div className="flex items-center justify-between">
                            <div className="text-sm font-medium">Tax slabs</div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setSlabs((prev) => [
                                  ...prev,
                                  { id: uid("slab"), from: 0, to: null, ratePercent: 0 },
                                ])
                              }
                            >
                              Add slab
                            </Button>
                          </div>

                          {errors.slabs && <div className="text-xs text-red-600 mt-2">{errors.slabs}</div>}

                          <div className="mt-3 rounded-md border">
                            <table className="w-full text-xs">
                              <thead className="bg-muted/30">
                                <tr className="text-left">
                                  <th className="px-2 py-2 font-semibold">From</th>
                                  <th className="px-2 py-2 font-semibold">To</th>
                                  <th className="px-2 py-2 font-semibold">%</th>
                                  <th className="px-2 py-2 font-semibold text-right"> </th>
                                </tr>
                              </thead>
                              <tbody>
                                {slabs.map((s) => (
                                  <tr key={s.id} className="border-t">
                                    <td className="px-2 py-2">
                                      <Input
                                        type="number"
                                        className="h-9 text-xs px-2"
                                        value={s.from}
                                        onChange={(e) =>
                                          setSlabs((prev) =>
                                            prev.map((x) =>
                                              x.id === s.id ? { ...x, from: Number(e.target.value) } : x
                                            )
                                          )
                                        }
                                      />
                                    </td>
                                    <td className="px-2 py-2">
                                      <Input
                                        type="number"
                                        className="h-9 text-xs px-2"
                                        value={s.to ?? ""}
                                        onChange={(e) =>
                                          setSlabs((prev) =>
                                            prev.map((x) =>
                                              x.id === s.id
                                                ? { ...x, to: e.target.value === "" ? null : Number(e.target.value) }
                                                : x
                                            )
                                          )
                                        }
                                      />
                                    </td>
                                    <td className="px-2 py-2">
                                      <Input
                                        type="number"
                                        className="h-9 text-xs px-2"
                                        value={s.ratePercent}
                                        onChange={(e) =>
                                          setSlabs((prev) =>
                                            prev.map((x) =>
                                              x.id === s.id
                                                ? { ...x, ratePercent: Number(e.target.value) }
                                                : x
                                            )
                                          )
                                        }
                                      />
                                    </td>
                                    <td className="px-2 py-2 text-right">
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-9 w-9 text-red-600 hover:text-red-700 hover:bg-red-50"
                                        onClick={() => setSlabs((prev) => prev.filter((x) => x.id !== s.id))}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        <div>
                          <div className="flex items-center justify-between">
                            <div className="text-sm font-medium">Surcharge</div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setSurcharges((prev) => [
                                  ...prev,
                                  { id: uid("sur"), from: 0, to: null, ratePercent: 0 },
                                ])
                              }
                            >
                              Add rule
                            </Button>
                          </div>

                          {errors.surcharges && (
                            <div className="text-xs text-red-600 mt-2">{errors.surcharges}</div>
                          )}

                          <div className="mt-3 rounded-md border">
                            <table className="w-full text-xs">
                              <thead className="bg-muted/30">
                                <tr className="text-left">
                                  <th className="px-2 py-2 font-semibold">From</th>
                                  <th className="px-2 py-2 font-semibold">To</th>
                                  <th className="px-2 py-2 font-semibold">%</th>
                                  <th className="px-2 py-2 font-semibold text-right"> </th>
                                </tr>
                              </thead>
                              <tbody>
                                {surcharges.map((r) => (
                                  <tr key={r.id} className="border-t">
                                    <td className="px-2 py-2">
                                      <Input
                                        type="number"
                                        className="h-9 text-xs px-2"
                                        value={r.from}
                                        onChange={(e) =>
                                          setSurcharges((prev) =>
                                            prev.map((x) =>
                                              x.id === r.id
                                                ? { ...x, from: Number(e.target.value) }
                                                : x
                                            )
                                          )
                                        }
                                      />
                                    </td>
                                    <td className="px-2 py-2">
                                      <Input
                                        type="number"
                                        className="h-9 text-xs px-2"
                                        value={r.to ?? ""}
                                        onChange={(e) =>
                                          setSurcharges((prev) =>
                                            prev.map((x) =>
                                              x.id === r.id
                                                ? { ...x, to: e.target.value === "" ? null : Number(e.target.value) }
                                                : x
                                            )
                                          )
                                        }
                                      />
                                    </td>
                                    <td className="px-2 py-2">
                                      <Input
                                        type="number"
                                        className="h-9 text-xs px-2"
                                        value={r.ratePercent}
                                        onChange={(e) =>
                                          setSurcharges((prev) =>
                                            prev.map((x) =>
                                              x.id === r.id
                                                ? { ...x, ratePercent: Number(e.target.value) }
                                                : x
                                            )
                                          )
                                        }
                                      />
                                    </td>
                                    <td className="px-2 py-2 text-right">
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-9 w-9 text-red-600 hover:text-red-700 hover:bg-red-50"
                                        onClick={() => setSurcharges((prev) => prev.filter((x) => x.id !== r.id))}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        <div className="rounded-md border bg-muted/20 p-3">
                          <div className="flex items-center justify-between gap-4">
                            <div>
                              <div className="text-sm font-medium">Education Cess (%)</div>
                              <div className="text-xs text-muted-foreground">
                                Applied on the calculated tax (+ surcharge).
                              </div>
                            </div>
                            <div className="w-[180px]">
                              <Input
                                type="number"
                                value={educationCessPercent}
                                onChange={(e) => setEducationCessPercent(Number(e.target.value))}
                              />
                              {errors.educationCessPercent && (
                                <div className="text-xs text-red-600 mt-1">
                                  {errors.educationCessPercent}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="text-xs text-muted-foreground">
                          Note: This is UI-only for now; we can wire it to backend once you confirm the table/design for storing methods & slabs.
                        </div>
                      </div>
                    </div>
                  </div>
                  )}

                </div>
              </TabsContent>

              <TabsContent value="pt">
                <div className="space-y-4">
                  {!isPtFormOpen && (
                    <div className="rounded-lg border bg-white p-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="w-full sm:max-w-[420px]">
                          <Input
                            value={ptSearch}
                            onChange={(e) => setPtSearch(e.target.value)}
                            placeholder="Search..."
                          />
                        </div>
                        <Button onClick={openNewPt}>Add New</Button>
                      </div>

                      <div className="mt-4 overflow-x-auto">
                        <table className="w-full min-w-[780px] text-sm">
                          <thead className="bg-muted/30">
                            <tr className="text-left">
                              <th className="px-3 py-2 font-semibold">State</th>
                              <th className="px-3 py-2 font-semibold">Effective From</th>
                              <th className="px-3 py-2 font-semibold">Updated on</th>
                              <th className="px-3 py-2 font-semibold">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ptLoading ? (
                              <tr className="border-t">
                                <td className="px-3 py-6 text-muted-foreground" colSpan={4}>
                                  Loading...
                                </td>
                              </tr>
                            ) : ptRules.length === 0 ? (
                              <tr className="border-t">
                                <td className="px-3 py-6 text-muted-foreground" colSpan={4}>
                                  {ptApiError ? ptApiError : "No records found."}
                                </td>
                              </tr>
                            ) : (
                              ptRules.map((r) => (
                                <tr key={r.id} className="border-t">
                                  <td className="px-3 py-3 font-medium">{r.stateName}</td>
                                  <td className="px-3 py-3">{formatDdMmmYyyy(r.effectiveFrom)}</td>
                                  <td className="px-3 py-3">{formatDdMmmYyyy(r.updatedOn)}</td>
                                  <td className="px-3 py-3">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      title="Edit"
                                      onClick={() => openEditPt(r)}
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div className="flex items-center gap-3">
                          <div className="text-sm text-muted-foreground">Rows per page</div>
                          <select
                            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                            value={ptPageSize}
                            onChange={(e) => setPtPageSize(Number(e.target.value))}
                          >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                        </div>

                        <div className="text-sm text-muted-foreground md:text-center">
                          <span className="font-medium text-foreground">{ptTotal}</span> record(s) total
                        </div>

                        <div className="flex items-center gap-3 md:justify-end">
                          <div className="text-sm text-muted-foreground">
                            Page <span className="font-medium text-foreground">{ptPage}</span> of{" "}
                            <span className="font-medium text-foreground">{ptTotalPages}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              disabled={ptPage <= 1}
                              onClick={() => setPtPage(1)}
                              title="First page"
                            >
                              <ChevronsLeft className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              disabled={ptPage <= 1}
                              onClick={() => setPtPage((p) => Math.max(1, p - 1))}
                              title="Previous page"
                            >
                              <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              disabled={ptPage >= ptTotalPages}
                              onClick={() => setPtPage((p) => Math.min(ptTotalPages, p + 1))}
                              title="Next page"
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              disabled={ptPage >= ptTotalPages}
                              onClick={() => setPtPage(ptTotalPages)}
                              title="Last page"
                            >
                              <ChevronsRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {isPtFormOpen && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div className="rounded-lg border bg-white p-4">
                        <div className="text-sm font-semibold">PT configuration</div>
                        <div className="text-sm text-muted-foreground mt-1">
                          Define PT slabs and calculation state-wise.
                        </div>

                        <div className="mt-4 space-y-4">
                          <div className="space-y-2">
                            <div className="text-sm font-medium">State</div>
                            <Popover open={ptStateOpen} onOpenChange={setPtStateOpen}>
                              <PopoverTrigger asChild>
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="w-full justify-between text-left font-normal"
                                >
                                  {ptStateName ? ptStateName : "Select state"}
                                  <ChevronsUpDown className="h-4 w-4 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-full p-2" align="start">
                                <Input
                                  value={ptStateSearch}
                                  onChange={(e) => setPtStateSearch(e.target.value)}
                                  placeholder="Search state..."
                                />
                                <div className="mt-2 max-h-[220px] overflow-auto space-y-1">
                                  {filteredPtStates.length === 0 ? (
                                    <div className="text-sm text-muted-foreground p-2">No states found.</div>
                                  ) : (
                                    filteredPtStates.map((s) => {
                                      const selected = s === ptStateName
                                      return (
                                        <button
                                          key={s}
                                          type="button"
                                          className="w-full flex items-center justify-between rounded-md px-2 py-2 text-sm hover:bg-accent"
                                          onClick={() => {
                                            setPtStateName(s)
                                            setPtStateOpen(false)
                                          }}
                                        >
                                          <span className="truncate">{s}</span>
                                          {selected && <Check className="h-4 w-4" />}
                                        </button>
                                      )
                                    })
                                  )}
                                </div>
                              </PopoverContent>
                            </Popover>
                            {ptStates.length === 0 && (
                              <div className="text-xs text-muted-foreground">
                                No states loaded. Restart backend after adding `GET /tenant-states/all`.
                              </div>
                            )}
                          </div>

                          <div className="space-y-2">
                            <div className="text-sm font-medium">Employment Category (all tenants)</div>
                            <div className="rounded-md border bg-muted/20 p-3 space-y-2">
                              <Input
                                value={ptEmploymentTypeSearch}
                                onChange={(e) => setPtEmploymentTypeSearch(e.target.value)}
                                placeholder="Search categories..."
                              />
                              <div className="flex items-center gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={selectAllPtEmploymentTypes}
                                >
                                  Select all
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={clearAllPtEmploymentTypes}
                                >
                                  Clear all
                                </Button>
                              </div>
                              <div className="max-h-[180px] overflow-auto space-y-2 text-sm">
                                {filteredPtEmploymentTypes.length === 0 ? (
                                  <div className="text-muted-foreground">No categories found.</div>
                                ) : (
                                  filteredPtEmploymentTypes.map((t) => (
                                    <label key={t} className="flex items-center gap-2">
                                      <input
                                        type="checkbox"
                                        checked={ptEmploymentTypes.includes(t)}
                                        onChange={() => togglePtEmploymentType(t)}
                                      />
                                      <span className="truncate">{t}</span>
                                    </label>
                                  ))
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <div className="text-sm font-medium">Country</div>
                            <select
                              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                              value={ptCountry}
                              onChange={(e) => setPtCountry(e.target.value)}
                            >
                              <option value="India">India</option>
                              <option value="United States">United States</option>
                              <option value="United Kingdom">United Kingdom</option>
                              <option value="Singapore">Singapore</option>
                              <option value="UAE">UAE</option>
                              <option value="Other">Other</option>
                            </select>
                          </div>

                          <div className="space-y-2">
                            <div className="text-sm font-medium">Effective from</div>
                            <DatePicker value={ptEffectiveFrom} onChange={setPtEffectiveFrom} />
                          </div>

                          <div className="space-y-2">
                            <div className="text-sm font-medium">Age criteria</div>
                            <div className="mt-2 grid grid-cols-2 gap-2">
                              <Input
                                type="number"
                                placeholder="Min age"
                                value={ptMinAge ?? ""}
                                onChange={(e) => setPtMinAge(e.target.value === "" ? null : Number(e.target.value))}
                              />
                              <Input
                                type="number"
                                placeholder="Max age"
                                value={ptMaxAge ?? ""}
                                onChange={(e) => setPtMaxAge(e.target.value === "" ? null : Number(e.target.value))}
                              />
                            </div>
                          </div>

                          <div className="space-y-2">
                            <div className="text-sm font-medium">
                              Tenants (no selection means applicable to all tenants)
                            </div>
                            <Input
                              value={ptTenantSearch}
                              onChange={(e) => setPtTenantSearch(e.target.value)}
                              placeholder="Search tenants..."
                            />
                            <div className="flex items-center gap-2">
                              <Button type="button" variant="outline" size="sm" onClick={selectAllPtTenants}>
                                Select all
                              </Button>
                              <Button type="button" variant="outline" size="sm" onClick={clearAllPtTenants}>
                                Clear all
                              </Button>
                            </div>
                            <div className="rounded-md border bg-muted/20 p-3 max-h-[220px] overflow-auto space-y-2 text-sm">
                              {filteredPtTenants.length === 0 ? (
                                <div className="text-muted-foreground">No tenants found.</div>
                              ) : (
                                filteredPtTenants.map((t) => (
                                  <label key={t.id} className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      checked={ptTenantIds.includes(t.id)}
                                      onChange={() => togglePtTenant(t.id)}
                                    />
                                    <span className="font-medium">{t.name}</span>
                                    <span className="text-muted-foreground">#{t.id}</span>
                                  </label>
                                ))
                              )}
                            </div>
                          </div>

                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              onClick={() => {
                                setIsPtFormOpen(false)
                                setEditingPtId(null)
                                resetPtForm()
                              }}
                            >
                              Close
                            </Button>
                            <Button onClick={savePt} disabled={ptSaving}>
                              {ptSaving ? "Saving..." : "Save"}
                            </Button>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border bg-white p-4">
                        <div className="text-sm font-semibold">PT slab</div>
                        <div className="text-sm text-muted-foreground mt-1">
                          Define slabs; you can edit the values as needed.
                        </div>

                        <div className="mt-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                          <div className="flex items-center gap-3">
                            <div className="text-sm font-medium">Frequency</div>
                            <select
                              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                              value={ptFrequency}
                              onChange={(e) => setPtFrequency(e.target.value as any)}
                            >
                              <option value="Monthly">Monthly</option>
                              <option value="Quarterly">Quarterly</option>
                              <option value="Half-yearly">Half-yearly</option>
                            </select>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setPtSlabs((prev) => [
                                ...prev,
                                { id: uid("pt"), from: 0, to: null, amount: 0 },
                              ])
                            }
                          >
                            Add slab
                          </Button>
                        </div>

                        <div className="mt-3 overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-muted/30">
                              <tr className="text-left">
                                <th className="px-3 py-2 font-semibold">From</th>
                                <th className="px-3 py-2 font-semibold">To</th>
                                <th className="px-3 py-2 font-semibold">PT Amount</th>
                                <th className="px-3 py-2 font-semibold text-right"> </th>
                              </tr>
                            </thead>
                            <tbody>
                              {ptSlabs.map((s) => (
                                <tr key={s.id} className="border-t">
                                  <td className="px-3 py-2">
                                    <Input
                                      type="number"
                                      value={s.from}
                                      onChange={(e) =>
                                        setPtSlabs((prev) =>
                                          prev.map((x) =>
                                            x.id === s.id ? { ...x, from: Number(e.target.value) } : x
                                          )
                                        )
                                      }
                                    />
                                  </td>
                                  <td className="px-3 py-2">
                                    <Input
                                      type="number"
                                      value={s.to ?? ""}
                                      onChange={(e) =>
                                        setPtSlabs((prev) =>
                                          prev.map((x) =>
                                            x.id === s.id
                                              ? { ...x, to: e.target.value === "" ? null : Number(e.target.value) }
                                              : x
                                          )
                                        )
                                      }
                                    />
                                  </td>
                                  <td className="px-3 py-2">
                                    <Input
                                      type="number"
                                      value={s.amount}
                                      onChange={(e) =>
                                        setPtSlabs((prev) =>
                                          prev.map((x) =>
                                            x.id === s.id ? { ...x, amount: Number(e.target.value) } : x
                                          )
                                        )
                                      }
                                    />
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-9 w-9 text-red-600 hover:text-red-700 hover:bg-red-50"
                                      onClick={() => setPtSlabs((prev) => prev.filter((x) => x.id !== s.id))}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="pf">
                <div className="space-y-4">
                  <div ref={pfTopRef} />

                  {!isPfFormOpen && (
                  <div className="rounded-lg border bg-white p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="w-full sm:max-w-[420px]">
                        <Input
                          value={pfSearch}
                          onChange={(e) => setPfSearch(e.target.value)}
                          placeholder="Search..."
                        />
                      </div>
                      <Button onClick={openNewPf}>Add New</Button>
                    </div>

                    <div className="mt-4 overflow-x-auto">
                      <table className="w-full min-w-[820px] text-sm">
                        <thead className="bg-muted/30">
                          <tr className="text-left">
                            <th className="px-3 py-2 font-semibold">Name</th>
                            <th className="px-3 py-2 font-semibold">Effective Date</th>
                            <th className="px-3 py-2 font-semibold">Updated on</th>
                            <th className="px-3 py-2 font-semibold">Status</th>
                            <th className="px-3 py-2 font-semibold">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pfLoading ? (
                            <tr className="border-t">
                              <td className="px-3 py-6 text-muted-foreground" colSpan={5}>
                                Loading...
                              </td>
                            </tr>
                          ) : pfRules.length === 0 ? (
                            <tr className="border-t">
                              <td className="px-3 py-6 text-muted-foreground" colSpan={5}>
                                {pfApiError ? pfApiError : "No records found."}
                              </td>
                            </tr>
                          ) : (
                            pfRules.map((r) => (
                              <tr key={r.id} className="border-t">
                                <td className="px-3 py-3 font-medium">{r.name}</td>
                                <td className="px-3 py-3">{formatDdMmmYyyy(r.effectiveDate)}</td>
                                <td className="px-3 py-3">{formatDdMmmYyyy(r.updatedOn)}</td>
                                <td className="px-3 py-3">
                                  <span
                                    className={
                                      r.status === "Active"
                                        ? "inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700"
                                        : "inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
                                    }
                                  >
                                    {r.status}
                                  </span>
                                </td>
                                <td className="px-3 py-3">
                                  <div className="flex items-center gap-1">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      title="Edit"
                                      onClick={() => openEditPf(r)}
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      title="Duplicate"
                                      onClick={() => duplicatePf(r)}
                                    >
                                      <Copy className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div className="flex items-center gap-3">
                        <div className="text-sm text-muted-foreground">Rows per page</div>
                        <select
                          className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                          value={pfPageSize}
                          onChange={(e) => setPfPageSize(Number(e.target.value))}
                        >
                          <option value={10}>10</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                          <option value={100}>100</option>
                        </select>
                      </div>

                      <div className="text-sm text-muted-foreground md:text-center">
                        <span className="font-medium text-foreground">{pfTotal}</span> record(s) total
                      </div>

                      <div className="flex items-center gap-3 md:justify-end">
                        <div className="text-sm text-muted-foreground">
                          Page <span className="font-medium text-foreground">{pfPage}</span> of{" "}
                          <span className="font-medium text-foreground">{pfTotalPages}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            disabled={pfPage <= 1}
                            onClick={() => setPfPage(1)}
                            title="First page"
                          >
                            <ChevronsLeft className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            disabled={pfPage <= 1}
                            onClick={() => setPfPage((p) => Math.max(1, p - 1))}
                            title="Previous page"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            disabled={pfPage >= pfTotalPages}
                            onClick={() => setPfPage((p) => Math.min(pfTotalPages, p + 1))}
                            title="Next page"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            disabled={pfPage >= pfTotalPages}
                            onClick={() => setPfPage(pfTotalPages)}
                            title="Last page"
                          >
                            <ChevronsRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>

                  )}

                  {isPfFormOpen && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div className="rounded-lg border bg-white p-4">
                        <div className="text-sm font-semibold">PF configuration</div>
                        <div className="text-sm text-muted-foreground mt-1">
                          Configure PF rules, applicability, and formula mapping.
                        </div>

                        <div className="mt-4 space-y-4">
                          <div className="space-y-2">
                            <div className="text-sm font-medium">Name</div>
                            <Input value={pfName} onChange={(e) => setPfName(e.target.value)} />
                            {pfErrors.pfName && <div className="text-xs text-red-600">{pfErrors.pfName}</div>}
                          </div>

                          <div className="space-y-2">
                            <div className="text-sm font-medium">Description</div>
                            <Textarea
                              value={pfDescription}
                              onChange={(e) => setPfDescription(e.target.value)}
                              className="min-h-[90px]"
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <div className="text-sm font-medium">Status</div>
                              <select
                                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                                value={pfStatus}
                                onChange={(e) => setPfStatus(e.target.value as any)}
                              >
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                              </select>
                            </div>

                            <div className="space-y-2">
                              <div className="text-sm font-medium">Country</div>
                              <select
                                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                                value={pfCountry}
                                onChange={(e) => setPfCountry(e.target.value)}
                              >
                                <option value="India">India</option>
                                <option value="United States">United States</option>
                                <option value="United Kingdom">United Kingdom</option>
                                <option value="Singapore">Singapore</option>
                                <option value="UAE">UAE</option>
                                <option value="Other">Other</option>
                              </select>
                              {pfErrors.pfCountry && <div className="text-xs text-red-600">{pfErrors.pfCountry}</div>}
                            </div>
                          </div>

                          <div className="space-y-2">
                            <div className="text-sm font-medium">Employment Category (all tenants)</div>
                            <div className="rounded-md border bg-muted/20 p-3 space-y-2">
                              <Input
                                value={pfEmploymentTypeSearch}
                                onChange={(e) => setPfEmploymentTypeSearch(e.target.value)}
                                placeholder="Search categories..."
                              />
                              <div className="flex items-center gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={selectAllPfEmploymentTypes}
                                >
                                  Select all
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={clearAllPfEmploymentTypes}
                                >
                                  Clear all
                                </Button>
                              </div>
                              <div className="max-h-[180px] overflow-auto space-y-2 text-sm">
                                {filteredPfEmploymentTypes.length === 0 ? (
                                  <div className="text-muted-foreground">No categories found.</div>
                                ) : (
                                  filteredPfEmploymentTypes.map((t) => (
                                    <label key={t} className="flex items-center gap-2">
                                      <input
                                        type="checkbox"
                                        checked={pfEmploymentTypes.includes(t)}
                                        onChange={() => togglePfEmploymentType(t)}
                                      />
                                      <span className="truncate">{t}</span>
                                    </label>
                                  ))
                                )}
                              </div>
                            </div>
                            {pfErrors.pfEmploymentTypes && (
                              <div className="text-xs text-red-600">{pfErrors.pfEmploymentTypes}</div>
                            )}
                          </div>

                          <div className="space-y-2">
                            <div className="text-sm font-medium">Effective date</div>
                            <DatePicker value={pfEffectiveDate} onChange={setPfEffectiveDate} />
                            {pfErrors.pfEffectiveDate && (
                              <div className="text-xs text-red-600 mt-1">{pfErrors.pfEffectiveDate}</div>
                            )}
                          </div>

                          <div className="space-y-2">
                            <div className="text-sm font-medium">Age criteria</div>
                            <div className="mt-2 grid grid-cols-2 gap-2">
                              <Input
                                type="number"
                                placeholder="Min age"
                                value={pfMinAge ?? ""}
                                onChange={(e) => setPfMinAge(e.target.value === "" ? null : Number(e.target.value))}
                              />
                              <Input
                                type="number"
                                placeholder="Max age"
                                value={pfMaxAge ?? ""}
                                onChange={(e) => setPfMaxAge(e.target.value === "" ? null : Number(e.target.value))}
                              />
                            </div>
                            {pfErrors.pfAge && <div className="text-xs text-red-600 mt-1">{pfErrors.pfAge}</div>}
                          </div>

                          <div className="space-y-2">
                            <div className="text-sm font-medium">
                              Tenants (no selection means applicable to all tenants)
                            </div>
                            <Input
                              value={pfTenantSearch}
                              onChange={(e) => setPfTenantSearch(e.target.value)}
                              placeholder="Search tenants..."
                            />
                            <div className="flex items-center gap-2">
                              <Button type="button" variant="outline" size="sm" onClick={selectAllPfTenants}>
                                Select all
                              </Button>
                              <Button type="button" variant="outline" size="sm" onClick={clearAllPfTenants}>
                                Clear all
                              </Button>
                            </div>
                            <div className="rounded-md border bg-muted/20 p-3 max-h-[220px] overflow-auto space-y-2 text-sm">
                              {filteredPfTenants().length === 0 ? (
                                <div className="text-muted-foreground">No tenants found.</div>
                              ) : (
                                filteredPfTenants().map((t) => (
                                  <label key={t.id} className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      checked={pfTenantIds.includes(t.id)}
                                      onChange={() => togglePfTenant(t.id)}
                                    />
                                    <span className="font-medium">{t.name}</span>
                                    <span className="text-muted-foreground">#{t.id}</span>
                                  </label>
                                ))
                              )}
                            </div>
                          </div>

                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              onClick={() => {
                                setIsPfFormOpen(false)
                                setEditingPfId(null)
                                resetPfForm()
                                queueMicrotask(() => scrollPfTop())
                              }}
                            >
                              Close
                            </Button>
                            <Button onClick={savePf} disabled={pfSaving}>
                              {pfSaving ? "Saving..." : "Save"}
                            </Button>
                          </div>
                        </div>
                        {pfApiError && <div className="text-xs text-red-600 text-right">{pfApiError}</div>}
                      </div>

                      <div className="rounded-lg border bg-white p-4">
                        <div className="text-sm font-semibold">PF Formula</div>
                        <div className="text-sm text-muted-foreground mt-1">
                          Formula grid is standard for every configuration. Formula columns are editable.
                        </div>

                        {pfErrors.pfFormulas && (
                          <div className="text-xs text-red-600 mt-2">{pfErrors.pfFormulas}</div>
                        )}

                        <div className="mt-3 overflow-x-auto">
                          <table className="w-full min-w-[820px] text-sm">
                            <thead className="bg-muted/30">
                              <tr className="text-left">
                                <th className="px-3 py-2 font-semibold">Account</th>
                                <th className="px-3 py-2 font-semibold">Monthly Formula (y-15k)</th>
                                <th className="px-3 py-2 font-semibold">Monthly Formula (y)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pfFormulas.map((r) => (
                                <tr key={r.id} className="border-t">
                                  <td className="px-3 py-3 font-medium">{r.account}</td>
                                  <td className="px-3 py-3">
                                    <Input
                                      value={r.formulaY15k}
                                      onChange={(e) =>
                                        setPfFormulas((prev) =>
                                          prev.map((x) =>
                                            x.id === r.id ? { ...x, formulaY15k: e.target.value } : x
                                          )
                                        )
                                      }
                                    />
                                  </td>
                                  <td className="px-3 py-3">
                                    <Input
                                      value={r.formulaY}
                                      onChange={(e) =>
                                        setPfFormulas((prev) =>
                                          prev.map((x) => (x.id === r.id ? { ...x, formulaY: e.target.value } : x))
                                        )
                                      }
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="esi">
                <div className="space-y-4">
                  <div ref={esiTopRef} />

                  {!isEsiFormOpen && (
                    <div className="rounded-lg border bg-white p-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="w-full sm:max-w-[420px]">
                          <Input
                            value={esiSearch}
                            onChange={(e) => setEsiSearch(e.target.value)}
                            placeholder="Search..."
                          />
                        </div>
                        <Button onClick={openNewEsi}>Add New</Button>
                      </div>

                      <div className="mt-4 overflow-x-auto">
                        <table className="w-full min-w-[920px] text-sm">
                          <thead className="bg-muted/30">
                            <tr className="text-left">
                              <th className="px-3 py-2 font-semibold">Name</th>
                              <th className="px-3 py-2 font-semibold">Effective Date</th>
                              <th className="px-3 py-2 font-semibold">Updated on</th>
                              <th className="px-3 py-2 font-semibold">Status</th>
                              <th className="px-3 py-2 font-semibold">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {esiLoading ? (
                              <tr className="border-t">
                                <td className="px-3 py-6 text-muted-foreground" colSpan={5}>
                                  Loading...
                                </td>
                              </tr>
                            ) : esiRules.length === 0 ? (
                              <tr className="border-t">
                                <td className="px-3 py-6 text-muted-foreground" colSpan={5}>
                                  {esiApiError ? esiApiError : "No records found."}
                                </td>
                              </tr>
                            ) : (
                              esiRules.map((r) => (
                                <tr key={r.id} className="border-t">
                                  <td className="px-3 py-3 font-medium">{r.name}</td>
                                  <td className="px-3 py-3">{formatDdMmmYyyy(r.effectiveDate)}</td>
                                  <td className="px-3 py-3">{formatDdMmmYyyy(r.updatedOn)}</td>
                                  <td className="px-3 py-3">
                                    <span
                                      className={
                                        r.status === "Active"
                                          ? "inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700"
                                          : "inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
                                      }
                                    >
                                      {r.status}
                                    </span>
                                  </td>
                                  <td className="px-3 py-3">
                                    <div className="flex items-center gap-2">
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        title="Edit"
                                        onClick={() => openEditEsi(r)}
                                      >
                                        <Pencil className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        title="Duplicate"
                                        onClick={() => duplicateEsi(r)}
                                      >
                                        <Copy className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div className="flex items-center gap-3">
                          <div className="text-sm text-muted-foreground">Rows per page</div>
                          <select
                            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                            value={esiPageSize}
                            onChange={(e) => setEsiPageSize(Number(e.target.value))}
                          >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                        </div>

                        <div className="text-sm text-muted-foreground md:text-center">
                          <span className="font-medium text-foreground">{esiTotal}</span> record(s) total
                        </div>

                        <div className="flex items-center gap-3 md:justify-end">
                          <div className="text-sm text-muted-foreground">
                            Page <span className="font-medium text-foreground">{esiPage}</span> of{" "}
                            <span className="font-medium text-foreground">{esiTotalPages}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              disabled={esiPage <= 1}
                              onClick={() => setEsiPage(1)}
                              title="First page"
                            >
                              <ChevronsLeft className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              disabled={esiPage <= 1}
                              onClick={() => setEsiPage((p) => Math.max(1, p - 1))}
                              title="Previous page"
                            >
                              <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              disabled={esiPage >= esiTotalPages}
                              onClick={() => setEsiPage((p) => Math.min(esiTotalPages, p + 1))}
                              title="Next page"
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              disabled={esiPage >= esiTotalPages}
                              onClick={() => setEsiPage(esiTotalPages)}
                              title="Last page"
                            >
                              <ChevronsRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {isEsiFormOpen && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div className="rounded-lg border bg-white p-4">
                        <div className="text-sm font-semibold">ESI configuration</div>
                        <div className="text-sm text-muted-foreground mt-1">
                          Configure ESI rules, applicability, and formula mapping.
                        </div>

                        <div className="mt-4 space-y-4">
                          <div className="space-y-2">
                            <div className="text-sm font-medium">Name</div>
                            <Input value={esiName} onChange={(e) => setEsiName(e.target.value)} />
                            {esiErrors.esiName && <div className="text-xs text-red-600">{esiErrors.esiName}</div>}
                          </div>

                          <div className="space-y-2">
                            <div className="text-sm font-medium">Description</div>
                            <Textarea
                              value={esiDescription}
                              onChange={(e) => setEsiDescription(e.target.value)}
                              className="min-h-[90px]"
                            />
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <div className="text-sm font-medium">Status</div>
                              <select
                                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                                value={esiStatus}
                                onChange={(e) => setEsiStatus(e.target.value as any)}
                              >
                                <option value="Active">Active</option>
                                <option value="Inactive">Inactive</option>
                              </select>
                            </div>

                            <div className="space-y-2">
                              <div className="text-sm font-medium">Country</div>
                              <select
                                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                                value={esiCountry}
                                onChange={(e) => setEsiCountry(e.target.value)}
                              >
                                <option value="India">India</option>
                                <option value="United States">United States</option>
                                <option value="United Kingdom">United Kingdom</option>
                                <option value="Singapore">Singapore</option>
                                <option value="UAE">UAE</option>
                                <option value="Other">Other</option>
                              </select>
                              {esiErrors.esiCountry && <div className="text-xs text-red-600">{esiErrors.esiCountry}</div>}
                            </div>
                          </div>

                          <div className="space-y-2">
                            <div className="text-sm font-medium">Employment Category (all tenants)</div>
                            <div className="rounded-md border bg-muted/20 p-3 space-y-2">
                              <Input
                                value={esiEmploymentTypeSearch}
                                onChange={(e) => setEsiEmploymentTypeSearch(e.target.value)}
                                placeholder="Search categories..."
                              />
                              <div className="flex items-center gap-2">
                                <Button type="button" variant="outline" size="sm" onClick={selectAllEsiEmploymentTypes}>
                                  Select all
                                </Button>
                                <Button type="button" variant="outline" size="sm" onClick={clearAllEsiEmploymentTypes}>
                                  Clear all
                                </Button>
                              </div>
                              <div className="max-h-[180px] overflow-auto space-y-2 text-sm">
                                {filteredEsiEmploymentTypes.length === 0 ? (
                                  <div className="text-muted-foreground">No categories found.</div>
                                ) : (
                                  filteredEsiEmploymentTypes.map((t) => (
                                    <label key={t} className="flex items-center gap-2">
                                      <input
                                        type="checkbox"
                                        checked={esiEmploymentTypes.includes(t as any)}
                                        onChange={() => toggleEsiEmploymentType(t as any)}
                                      />
                                      <span className="truncate">{t}</span>
                                    </label>
                                  ))
                                )}
                              </div>
                            </div>
                            {esiErrors.esiEmploymentTypes && (
                              <div className="text-xs text-red-600">{esiErrors.esiEmploymentTypes}</div>
                            )}
                          </div>

                          <div className="space-y-2">
                            <div className="text-sm font-medium">Effective date</div>
                            <DatePicker value={esiEffectiveDate} onChange={setEsiEffectiveDate} />
                            {esiErrors.esiEffectiveDate && (
                              <div className="text-xs text-red-600 mt-1">{esiErrors.esiEffectiveDate}</div>
                            )}
                          </div>

                          <div className="space-y-2">
                            <div className="text-sm font-medium">Age criteria</div>
                            <div className="mt-2 grid grid-cols-2 gap-2">
                              <Input
                                type="number"
                                placeholder="Min age"
                                value={esiMinAge ?? ""}
                                onChange={(e) => setEsiMinAge(e.target.value === "" ? null : Number(e.target.value))}
                              />
                              <Input
                                type="number"
                                placeholder="Max age"
                                value={esiMaxAge ?? ""}
                                onChange={(e) => setEsiMaxAge(e.target.value === "" ? null : Number(e.target.value))}
                              />
                            </div>
                            {esiErrors.esiAge && <div className="text-xs text-red-600 mt-1">{esiErrors.esiAge}</div>}
                          </div>

                          <div className="space-y-2">
                            <div className="text-sm font-medium">
                              Tenants (no selection means applicable to all tenants)
                            </div>
                            <Input
                              value={esiTenantSearch}
                              onChange={(e) => setEsiTenantSearch(e.target.value)}
                              placeholder="Search tenants..."
                            />
                            <div className="flex items-center gap-2">
                              <Button type="button" variant="outline" size="sm" onClick={selectAllEsiTenants}>
                                Select all
                              </Button>
                              <Button type="button" variant="outline" size="sm" onClick={clearAllEsiTenants}>
                                Clear all
                              </Button>
                            </div>
                            <div className="rounded-md border bg-muted/20 p-3 max-h-[220px] overflow-auto space-y-2 text-sm">
                              {filteredEsiTenants().length === 0 ? (
                                <div className="text-muted-foreground">No tenants found.</div>
                              ) : (
                                filteredEsiTenants().map((t) => (
                                  <label key={t.id} className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      checked={esiTenantIds.includes(t.id)}
                                      onChange={() => toggleEsiTenant(t.id)}
                                    />
                                    <span className="font-medium">{t.name}</span>
                                    <span className="text-muted-foreground">#{t.id}</span>
                                  </label>
                                ))
                              )}
                            </div>
                          </div>

                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              onClick={() => {
                                setIsEsiFormOpen(false)
                                setEditingEsiId(null)
                                resetEsiForm()
                                queueMicrotask(() => scrollEsiTop())
                              }}
                            >
                              Close
                            </Button>
                            <Button onClick={saveEsi} disabled={esiSaving}>
                              {esiSaving ? "Saving..." : "Save"}
                            </Button>
                          </div>
                        </div>
                        {esiApiError && <div className="text-xs text-red-600 text-right">{esiApiError}</div>}
                      </div>

                      <div className="rounded-lg border bg-white p-4">
                        <div className="text-sm font-semibold">ESI Formula</div>
                        <div className="text-sm text-muted-foreground mt-1">
                          Formula grid is standard for every configuration. Formula columns are editable.
                        </div>

                        {esiErrors.esiFormulas && <div className="text-xs text-red-600 mt-2">{esiErrors.esiFormulas}</div>}

                        <div className="mt-3 overflow-x-auto">
                          <table className="w-full min-w-[820px] text-sm">
                            <thead className="bg-muted/30">
                              <tr className="text-left">
                                <th className="px-3 py-2 font-semibold">Account</th>
                                <th className="px-3 py-2 font-semibold">Monthly formula</th>
                              </tr>
                            </thead>
                            <tbody>
                              {esiFormulas.map((r) => (
                                <tr key={r.id} className="border-t">
                                  <td className="px-3 py-3 font-medium">{r.account}</td>
                                  <td className="px-3 py-3">
                                    <Input
                                      value={r.monthlyFormula}
                                      onChange={(e) =>
                                        setEsiFormulas((prev) =>
                                          prev.map((x) => (x.id === r.id ? { ...x, monthlyFormula: e.target.value } : x))
                                        )
                                      }
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="lwf">
                <div className="space-y-4">
                  {!isLwfFormOpen && (
                    <div className="rounded-lg border bg-white p-4">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="w-full sm:max-w-[420px]">
                          <Input
                            value={lwfSearch}
                            onChange={(e) => setLwfSearch(e.target.value)}
                            placeholder="Search..."
                          />
                        </div>
                        <Button onClick={openNewLwf}>Add New</Button>
                      </div>

                      <div className="mt-4 overflow-x-auto">
                        <table className="w-full min-w-[780px] text-sm">
                          <thead className="bg-muted/30">
                            <tr className="text-left">
                              <th className="px-3 py-2 font-semibold">State</th>
                              <th className="px-3 py-2 font-semibold">Effective From</th>
                              <th className="px-3 py-2 font-semibold">Updated on</th>
                              <th className="px-3 py-2 font-semibold">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lwfLoading ? (
                              <tr className="border-t">
                                <td className="px-3 py-6 text-muted-foreground" colSpan={4}>
                                  Loading...
                                </td>
                              </tr>
                            ) : lwfRules.length === 0 ? (
                              <tr className="border-t">
                                <td className="px-3 py-6 text-muted-foreground" colSpan={4}>
                                  {lwfApiError ? lwfApiError : "No records found."}
                                </td>
                              </tr>
                            ) : (
                              lwfRules.map((r) => (
                                <tr key={r.id} className="border-t">
                                  <td className="px-3 py-3 font-medium">{r.stateName}</td>
                                  <td className="px-3 py-3">{formatDdMmmYyyy(r.effectiveFrom)}</td>
                                  <td className="px-3 py-3">{formatDdMmmYyyy(r.updatedOn)}</td>
                                  <td className="px-3 py-3">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      title="Edit"
                                      onClick={() => openEditLwf(r)}
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div className="flex items-center gap-3">
                          <div className="text-sm text-muted-foreground">Rows per page</div>
                          <select
                            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                            value={lwfPageSize}
                            onChange={(e) => setLwfPageSize(Number(e.target.value))}
                          >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                        </div>

                        <div className="text-sm text-muted-foreground md:text-center">
                          <span className="font-medium text-foreground">{lwfTotal}</span> record(s) total
                        </div>

                        <div className="flex items-center gap-3 md:justify-end">
                          <div className="text-sm text-muted-foreground">
                            Page <span className="font-medium text-foreground">{lwfPage}</span> of{" "}
                            <span className="font-medium text-foreground">{lwfTotalPages}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              disabled={lwfPage <= 1}
                              onClick={() => setLwfPage(1)}
                              title="First page"
                            >
                              <ChevronsLeft className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              disabled={lwfPage <= 1}
                              onClick={() => setLwfPage((p) => Math.max(1, p - 1))}
                              title="Previous page"
                            >
                              <ChevronLeft className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              disabled={lwfPage >= lwfTotalPages}
                              onClick={() => setLwfPage((p) => Math.min(lwfTotalPages, p + 1))}
                              title="Next page"
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="icon"
                              disabled={lwfPage >= lwfTotalPages}
                              onClick={() => setLwfPage(lwfTotalPages)}
                              title="Last page"
                            >
                              <ChevronsRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {isLwfFormOpen && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      <div className="rounded-lg border bg-white p-4">
                        <div className="text-sm font-semibold">LWF configuration</div>
                        <div className="text-sm text-muted-foreground mt-1">
                          Define LWF slabs and calculation state-wise.
                        </div>

                        <div className="mt-4 space-y-4">
                          <div className="space-y-2">
                            <div className="text-sm font-medium">State</div>
                            <Popover open={lwfStateOpen} onOpenChange={setLwfStateOpen}>
                              <PopoverTrigger asChild>
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="w-full justify-between text-left font-normal"
                                >
                                  {lwfStateName ? lwfStateName : "Select state"}
                                  <ChevronsUpDown className="h-4 w-4 opacity-50" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-full p-2" align="start">
                                <Input
                                  value={lwfStateSearch}
                                  onChange={(e) => setLwfStateSearch(e.target.value)}
                                  placeholder="Search state..."
                                />
                                <div className="mt-2 max-h-[220px] overflow-auto space-y-1">
                                  {filteredLwfStates.length === 0 ? (
                                    <div className="text-sm text-muted-foreground p-2">No states found.</div>
                                  ) : (
                                    filteredLwfStates.map((s) => {
                                      const selected = s === lwfStateName
                                      return (
                                        <button
                                          key={s}
                                          type="button"
                                          className="w-full flex items-center justify-between rounded-md px-2 py-2 text-sm hover:bg-accent"
                                          onClick={() => {
                                            setLwfStateName(s)
                                            setLwfStateOpen(false)
                                          }}
                                        >
                                          <span className="truncate">{s}</span>
                                          {selected && <Check className="h-4 w-4" />}
                                        </button>
                                      )
                                    })
                                  )}
                                </div>
                              </PopoverContent>
                            </Popover>
                          </div>

                          <div className="space-y-2">
                            <div className="text-sm font-medium">Employment Category (all tenants)</div>
                            <div className="rounded-md border bg-muted/20 p-3 space-y-2">
                              <Input
                                value={lwfEmploymentTypeSearch}
                                onChange={(e) => setLwfEmploymentTypeSearch(e.target.value)}
                                placeholder="Search categories..."
                              />
                              <div className="flex items-center gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={selectAllLwfEmploymentTypes}
                                >
                                  Select all
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={clearAllLwfEmploymentTypes}
                                >
                                  Clear all
                                </Button>
                              </div>
                              <div className="max-h-[180px] overflow-auto space-y-2 text-sm">
                                {filteredLwfEmploymentTypes.length === 0 ? (
                                  <div className="text-muted-foreground">No categories found.</div>
                                ) : (
                                  filteredLwfEmploymentTypes.map((t) => (
                                    <label key={t} className="flex items-center gap-2">
                                      <input
                                        type="checkbox"
                                        checked={lwfEmploymentTypes.includes(t)}
                                        onChange={() => toggleLwfEmploymentType(t)}
                                      />
                                      <span className="truncate">{t}</span>
                                    </label>
                                  ))
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <div className="text-sm font-medium">Country</div>
                            <select
                              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                              value={lwfCountry}
                              onChange={(e) => setLwfCountry(e.target.value)}
                            >
                              <option value="India">India</option>
                              <option value="United States">United States</option>
                              <option value="United Kingdom">United Kingdom</option>
                              <option value="Singapore">Singapore</option>
                              <option value="UAE">UAE</option>
                              <option value="Other">Other</option>
                            </select>
                          </div>

                          <div className="space-y-2">
                            <div className="text-sm font-medium">Effective from</div>
                            <DatePicker value={lwfEffectiveFrom} onChange={setLwfEffectiveFrom} />
                          </div>

                          <div className="space-y-2">
                            <div className="text-sm font-medium">Age criteria</div>
                            <div className="mt-2 grid grid-cols-2 gap-2">
                              <Input
                                type="number"
                                placeholder="Min age"
                                value={lwfMinAge ?? ""}
                                onChange={(e) =>
                                  setLwfMinAge(e.target.value === "" ? null : Number(e.target.value))
                                }
                              />
                              <Input
                                type="number"
                                placeholder="Max age"
                                value={lwfMaxAge ?? ""}
                                onChange={(e) =>
                                  setLwfMaxAge(e.target.value === "" ? null : Number(e.target.value))
                                }
                              />
                            </div>
                          </div>

                          <div className="space-y-2">
                            <div className="text-sm font-medium">
                              Tenants (no selection means applicable to all tenants)
                            </div>
                            <Input
                              value={lwfTenantSearch}
                              onChange={(e) => setLwfTenantSearch(e.target.value)}
                              placeholder="Search tenants..."
                            />
                            <div className="flex items-center gap-2">
                              <Button type="button" variant="outline" size="sm" onClick={selectAllLwfTenants}>
                                Select all
                              </Button>
                              <Button type="button" variant="outline" size="sm" onClick={clearAllLwfTenants}>
                                Clear all
                              </Button>
                            </div>
                            <div className="rounded-md border bg-muted/20 p-3 max-h-[220px] overflow-auto space-y-2 text-sm">
                              {filteredLwfTenants.length === 0 ? (
                                <div className="text-muted-foreground">No tenants found.</div>
                              ) : (
                                filteredLwfTenants.map((t) => (
                                  <label key={t.id} className="flex items-center gap-2">
                                    <input
                                      type="checkbox"
                                      checked={lwfTenantIds.includes(t.id)}
                                      onChange={() => toggleLwfTenant(t.id)}
                                    />
                                    <span className="font-medium">{t.name}</span>
                                    <span className="text-muted-foreground">#{t.id}</span>
                                  </label>
                                ))
                              )}
                            </div>
                          </div>

                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              onClick={() => {
                                setIsLwfFormOpen(false)
                                setEditingLwfId(null)
                                resetLwfForm()
                              }}
                            >
                              Close
                            </Button>
                            <Button onClick={saveLwf} disabled={lwfSaving}>
                              {lwfSaving ? "Saving..." : "Save"}
                            </Button>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border bg-white p-4">
                        <div className="text-sm font-semibold">LWF slab</div>
                        <div className="text-sm text-muted-foreground mt-1">
                          Define slabs; you can edit the values as needed.
                        </div>

                        <div className="mt-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                          <div className="flex items-center gap-3">
                            <div className="text-sm font-medium">Frequency</div>
                            <select
                              className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                              value={lwfFrequency}
                              onChange={(e) => setLwfFrequency(e.target.value as any)}
                            >
                              <option value="Monthly">Monthly</option>
                              <option value="Quarterly">Quarterly</option>
                              <option value="Half-yearly">Half-yearly</option>
                            </select>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setLwfSlabs((prev) => [
                                ...prev,
                                { id: uid("lwf"), from: 0, to: null, employeeAmount: 0, employerAmount: 0 },
                              ])
                            }
                          >
                            Add slab
                          </Button>
                        </div>

                        <div className="mt-3 overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-muted/30">
                              <tr className="text-left">
                                <th className="px-3 py-2 font-semibold">From</th>
                                <th className="px-3 py-2 font-semibold">To</th>
                                <th className="px-3 py-2 font-semibold">Employee LWF</th>
                                <th className="px-3 py-2 font-semibold">Employer LWF</th>
                                <th className="px-3 py-2 font-semibold text-right"> </th>
                              </tr>
                            </thead>
                            <tbody>
                              {lwfSlabs.map((s) => (
                                <tr key={s.id} className="border-t">
                                  <td className="px-3 py-2">
                                    <Input
                                      type="number"
                                      value={s.from}
                                      onChange={(e) =>
                                        setLwfSlabs((prev) =>
                                          prev.map((x) =>
                                            x.id === s.id ? { ...x, from: Number(e.target.value) } : x
                                          )
                                        )
                                      }
                                    />
                                  </td>
                                  <td className="px-3 py-2">
                                    <Input
                                      type="number"
                                      value={s.to ?? ""}
                                      onChange={(e) =>
                                        setLwfSlabs((prev) =>
                                          prev.map((x) =>
                                            x.id === s.id
                                              ? { ...x, to: e.target.value === "" ? null : Number(e.target.value) }
                                              : x
                                          )
                                        )
                                      }
                                    />
                                  </td>
                                  <td className="px-3 py-2">
                                    <Input
                                      type="number"
                                      value={s.employeeAmount}
                                      onChange={(e) =>
                                        setLwfSlabs((prev) =>
                                          prev.map((x) =>
                                            x.id === s.id ? { ...x, employeeAmount: Number(e.target.value) } : x
                                          )
                                        )
                                      }
                                    />
                                  </td>
                                  <td className="px-3 py-2">
                                    <Input
                                      type="number"
                                      value={s.employerAmount}
                                      onChange={(e) =>
                                        setLwfSlabs((prev) =>
                                          prev.map((x) =>
                                            x.id === s.id ? { ...x, employerAmount: Number(e.target.value) } : x
                                          )
                                        )
                                      }
                                    />
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-9 w-9 text-red-600 hover:text-red-700 hover:bg-red-50"
                                      onClick={() => setLwfSlabs((prev) => prev.filter((x) => x.id !== s.id))}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="gratuity">
                <div className="rounded-lg border bg-muted/20 p-4 text-sm text-muted-foreground">
                  Configure gratuity eligibility, vesting rules, and calculation parameters.
                </div>
              </TabsContent>

              <TabsContent value="stat-bonus">
                <div className="rounded-lg border bg-muted/20 p-4 text-sm text-muted-foreground">
                  Configure statutory bonus eligibility, min/max %, and salary ceilings.
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </>
  )
}

